import * as React from 'react';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, Navigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import axios, { isAxiosError } from 'axios';
import { ThemeProvider } from '@mui/material/styles';

// @mui
import {
  Card,
  Stack,
  Container,
  Typography,
  Tabs,
  Tab,
  Box,
  List,
  ListItem,
  Avatar,
  Grid,
  FormControlLabel,
  TextField,
  Switch,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableContainer,
  TableFooter,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  CircularProgress,
  CardHeader,
  TableCell,
  Tooltip,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Menu,
  FormGroup,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
import { minWidth, styled } from '@mui/system';
//--------------------------------------------------------
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Assignment as AssignmentIcon,
  Badge as BadgeIcon,
  GppGood as GppGoodIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Check as CheckIcon,
  Autorenew as AutorenewIcon,
} from '@mui/icons-material';
//--------------------------------------------------------
import {
  PopupSuccessBody,
  PopupErrorBody,
  POPUP_DURATION_DELAY,
  POPUP_DURATION_SUCCESS,
  POPUP_DURATION_ERROR,
} from '../../utils/popUpStyles';
import { CustomAutocomplete, CustomTextField, CustomDatePicker } from '../../utils/StyledInputs';
import { CustomSnackbar, CreateSnackbarFunctions } from '../../utils/Snackbar';
import { TablePaginationActions, theme } from '../../utils/TableStyles';
import { ErrorUI, HttpErrorHandler } from '../../utils/ErrorHandler';
import { PositionStatusLabel } from '../../utils/PositionStatusLabel';
import { QuintupleTabPanel, a11yProps } from '../../utils/TabStyles';
import { EditPositionDialog } from '../../utils/EditPositionDialog';
import { NewPositionDialog } from '../../utils/NewPositionDialog';
import { validatePhoneNumber } from '../../utils/numberValidity';
import { EntitiesDialog } from '../../utils/EntitiesDialog';
import { DecisionDialog } from '../../utils/DecisionDialog';
import { LoadingUI } from '../../utils/LayoutStyles';
import GenderList from '../../model/gender';
import {
  DefaultOutlinedButton,
  DefaultContainedButton,
  RedOutlinedButton,
  CustomIconButton,
} from '../../utils/Buttons';
import { CommonLabel } from '../../utils/Common';
import API from '../../api';

// ----------------------------------------------------------------------

// custom popup style
const Popup = styled(BasePopup)`
  z-index: 1400;
`;

dayjs.locale('it');

export default function UserDetails() {
  // general
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const { state } = useLocation();
  const userID = state.userID;
  const isSmallScreen = useMediaQuery('(min-width: 600px)');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isEditApisRunning, setIsEditApisRunning] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState('noDialog');
  const [currentProfile, setCurrentProfile] = useState('');

  // popup handler
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [popUpText, setPopUpText] = useState('');
  const [popupSuccess, setPopupSuccess] = useState('');
  const showPopup = (success, text) => {
    setPopupSuccess(success);
    setOpen(true);

    if (text) {
      setPopUpText(text);
    }

    setTimeout(
      () => {
        setOpen(false);
      },
      success ? POPUP_DURATION_SUCCESS : POPUP_DURATION_ERROR
    );
  };

  // popup component
  const displayPopup = () => {
    return (
      <Container ref={setAnchor} sx={{ position: 'absolute', bottom: '-20px' }}>
        <Popup anchor={anchor} open={open}>
          {popupSuccess ? (
            <PopupSuccessBody sx={{ whiteSpace: 'pre-line' }}>{popUpText}</PopupSuccessBody>
          ) : (
            <PopupErrorBody>{popUpText}</PopupErrorBody>
          )}
        </Popup>
      </Container>
    );
  };

  // handle snackbars
  const {
    open: openEditUserDataSnack,
    handleOpen: handleOpenEditUserDataSnack,
    handleClose: handleCloseEditUserDataSnack,
  } = CreateSnackbarFunctions(false);

  // Snackbar
  const {
    open: openNewAuthDataSnack,
    handleOpen: handleOpenNewAuthDataSnack,
    handleClose: handleCloseNewAuthDataSnack,
  } = CreateSnackbarFunctions(false);

  // Snackbar handler
  const [onOpenSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarData, setSnackbarData] = useState({
    text: null,
    color: 'warning',
  });
  const onSnackBarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar(false);
  };

  const [errors, setErrors] = useState({
    name: false,
    surname: false,
    fiscal_code: false,
    email: false,
    birth_date: false,
    place_of_birth: false,
    gender: false,
    name_alias: false,
    phone: false,
    call_code: false,
    home_country_id: false,
    home_street: false,
    home_city_id: false,
    home_zip_code: false,
    home_city_name: false,
    home_province_id: false,
    home_region_name: false,
    res_country_id: false,
    res_street: false,
    res_zip_code: false,
    res_city_id: false,
    res_city_name: false,
    res_province_id: false,
    res_region_name: false,
    entity_id: false,
    user_roles: false,
  });

  // tabs handler
  // user section tab
  const [value, setValue] = useState(0);
  const handleChangeUserTabs = (event, newValue) => {
    setValue(newValue);
  };

  // position tab
  const [positionTabValue, setPositionTabValue] = useState(0);
  const handleChangePositionTab = (event, newValue) => {
    setPositionTabValue(newValue);
  };

  /* ------------------------------ User details ------------------------------ */
  const [canModifyUser, setCanModifyUser] = useState(false);
  const [canDeleteCards, setCanDeleteCards] = useState(false);
  const [applyPaymentStatusFilter, setApplyPaymentStatusFilter] = useState(false);
  const [canManageUser, setCanManageUser] = useState(false);
  const [canManageUserRoles, setCanManageUserRoles] = useState(false);

  // Personal Data
  const [name, setName] = useState();
  const [surname, setSurname] = useState();
  const [aliasName, setAliasName] = useState();
  const [useAlias, setUseAlias] = useState(false);
  const [gender, setGender] = useState();
  const [fiscalCode, setFiscalCode] = useState();
  const [email, setEmail] = useState();
  const [birthDate, setBirthDate] = useState();
  const [birthPlace, setBirthPlace] = useState();
  const [phoneNumber, setPhoneNumber] = useState();
  const [callCode, setCallCode] = useState({});
  const [verifiedUser, setVerifiedUser] = useState();
  const [onlineUser, setOnlineUser] = useState();
  const [lockedUser, setLockedUser] = useState();
  const [userPosition, setUserPosition] = useState({});
  const [userEntity, setUserEntity] = useState({});
  const [isBlacklistedEntity, setIsBlacklistedEntity] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [isGdAvailable, setIsGdAvailable] = useState(false);

  useEffect(() => {
    if (birthDate) {
      const thirtyYearsAgo = dayjs().subtract(30, 'year');
      const hasLessThan30Years = dayjs(birthDate).isAfter(dayjs(thirtyYearsAgo));
      setIsGdAvailable(hasLessThan30Years);
    }
  }, [birthDate]);

  // user static data
  const [userInfo, setUserInfo] = useState({});
  const [resRegionList, setResRegionList] = useState([]);
  const [resCountriesList, setResCountriesList] = useState([]);
  const [resProvincesList, setResProvincesList] = useState([]);
  const [resCitiesList, setResCitiesList] = useState([]);
  const [domRegionList, setDomRegionList] = useState([]);
  const [domCountriesList, setDomCountriesList] = useState([]);
  const [domProvincesList, setDomProvincesList] = useState([]);
  const [domCitiesList, setDomCitiesList] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [positions, setPositions] = useState([]);
  const [birthPlacesList, setBirthPlacesList] = useState([]);
  const [intPositions, setIntPositions] = useState([]);
  const [extPositions, setExtPositions] = useState([]);
  const [displayedPositions, setDisplayedPositions] = useState([]);
  const [EntitiesList, setEntitiesList] = useState([]);
  const [cards, setCards] = useState([]);
  const [italy, setItaly] = useState({});

  // Newsletter subscription
  const [allowNews, setAllowNews] = useState(Boolean(userInfo.allow_news));

  // user rates
  const [userRates, setUserRates] = useState([]);
  const [totalPrice, setTotalPrice] = useState([]);
  const [activeRatesApiCalled, setActiveRatesApiCalled] = useState(false);
  const [subscriptionApiError, setSubscriptionApiError] = useState('');
  const [expiredSubscriptionApiError, setExpiredSubscriptionApiError] = useState('');
  const [disabledStates, setDisabledStates] = useState(userRates.map(() => false));
  const [activeCampaign, setActiveCampaign] = useState({});
  // Residence
  const [resCountry, setResCountry] = useState({});
  const [resRegion, setResRegion] = useState({});
  const [resProvince, setResProvince] = useState({});
  const [resCity, setResCity] = useState({});
  const [resStreet, setResStreet] = useState('');
  const [resZipCode, setResZipCode] = useState('');
  const [resCityName, setResCityName] = useState(null);
  const [resRegionName, setResRegionName] = useState(null);
  
  // Domicile
  const [domCountry, setDomCountry] = useState({});
  const [domRegion, setDomRegion] = useState({});
  const [domProvince, setDomProvince] = useState({});
  const [domCity, setDomCity] = useState({});
  const [domStreet, setDomStreet] = useState('');
  const [domZipCode, setDomZipCode] = useState('');
  const [domIsRes, setDomIsRes] = useState();
  const [domCityName, setDomCityName] = useState(null);
  const [domRegionName, setDomRegionName] = useState(null);
  
  // get user rates
  useEffect(() => {
    if (!isLoading) {
      if (positions.length > 0) {
        let userHighestPosition = {};

        userHighestPosition = positions.reduce((maxPosition, currentPosition) => {
          return currentPosition.level > maxPosition.level ? currentPosition : maxPosition;
        });
        setUserPosition(userHighestPosition);
      }
    }
  }, [positions]);

  // Get Active Rates Api
  const getActiveRates = (campaignID = '') => {

    let url;
    if (campaignID.length === 0) {
      url = `admin/users/active_rates?user_id=${userID}&entity_id=${userInfo.entity_id}`;
    } else {
      url = `admin/users/active_rates?user_id=${userID}&entity_id=${userInfo.entity_id}&campaign_id=${campaignID}`;
    }

    API.get(url)
      .then((response) => {
        const freeOffer = response.data.free_offer;
        const entityRates = response.data.entity_rate.map((obj) => obj.rate);
        const positionRate = response.data.position_rate ? response.data.position_rate.rate : {};

        const isPositionRateEmpty = Object.keys(positionRate).length === 0 && positionRate.constructor === Object;

        const activeRates = [freeOffer, ...entityRates];
        if (!isPositionRateEmpty) {
          activeRates.push(positionRate);
        }
        if (campaignID.length === 0) {
          setUserRates(activeRates);

          setActiveRatesApiCalled(true);
        } else {
          setExpiredRates(activeRates);
          setExpiredRatesApiCalled(true);
        }
      })
      .catch((error) => {
        HttpErrorHandler(error, setSubscriptionApiError);
      });
    
  };

  const getActiveCampaign = () => {
    if (Object.keys(activeCampaign).length === 0) {
      API.get('/active_campaign')
        .then((response) => {
          setActiveCampaign(response.data);
          getActiveRates();
        })
        .catch((error) => {
          HttpErrorHandler(error, setSubscriptionApiError);
        });
    } else {
      getActiveRates();
    }
  };

  useEffect(() => {
    if (!useAlias) {
      setAliasName(null);
    }
  }, [useAlias]);

  // Get Rates Total Value
  useEffect(() => {
    let ratesSum;

    if (isGdAvailable) {
      ratesSum = userRates.reduce((sum, rate) => sum + Number(rate.price_discounted), 0);
    } else {
      ratesSum = userRates.reduce((sum, rate) => sum + Number(rate.price), 0);
    }
    setTotalPrice(ratesSum);
  }, [userRates]);

  // get user informations and positions list
  useEffect(() => {
    getUserInfo();

    checkAuthorization('user_supervisor', setCanManageUser);
    checkAuthorization('user_auth', setCanManageUserRoles);
    checkAuthorization('delete_auth', setCanDeleteCards);
  }, []);

  const checkAuthorization = (authKey, setState) => {
    const userData = JSON.parse(localStorage.getItem('userData'));
    const authorization = userData[authKey];

    if (authorization) {
      setState(true);
    }
  };

  // Get User Info Api
  const getUserInfo = () => {
    axios
      .all([API.get(`/users/${userID}`)])
      .then(
        axios.spread((userResponse) => {
          setCanModifyUser(userResponse.data.auth_type === 'W' || userResponse.data.auth_type === 'D');
          setUserInfo(userResponse.data);
          setCards(userResponse.data.cards);
          setUserRoles(userResponse.data.roles);
          setPositions(userResponse.data.positions);

          // Utils
          setDomIsRes(Boolean(userResponse.data.res_equal_home));
          setDomZipCode(userResponse.data.home_zip_code);
          setDomStreet(userResponse.data.home_street);
          setResZipCode(userResponse.data.res_zip_code);
          setResStreet(userResponse.data.res_street);

          // Personal Data
          setName(userResponse.data.name);
          setSurname(userResponse.data.surname);
          setAliasName(userResponse.data.name_alias);
          if (userResponse.data.name_alias && userResponse.data.name_alias.length > 0) {
            setUseAlias(true);
          }
          getDisplayableGender(userResponse.data.gender);
          setFiscalCode(userResponse.data.fiscal_code);
          setBirthDate(userResponse.data.birth_date);
          setBirthPlace(userResponse.data.place_of_birth);
          setEmail(userResponse.data.email);
          setPhoneNumber(userResponse.data.phone);
          setCallCode(userResponse.data.call_code);
          setVerifiedUser(userResponse.data.verified);
          setOnlineUser(userResponse.data.user_online);
          setLockedUser(userResponse.data.blocked);

          const userEntities = userResponse.data.entities;
          const userMasterID = userResponse.data.entity_id;
          const userMasterEntity = userEntities.find((entity) => entity.id === userMasterID);
          setUserEntity(userMasterEntity);
          setIsBlacklistedEntity(userResponse.data.blacklisted_entity);

          // News preference
          setAllowNews(Boolean(userResponse.data.allow_news));

          // Residence
          setResCountry(userResponse.data.res_country);
          setResRegion(userResponse.data.res_region);
          setResProvince(userResponse.data.res_province);
          setResCity(userResponse.data.res_city);
          setResCityName(userResponse.data.res_city_name);
          setResRegionName(userResponse.data.res_region_name);

          // Domicile
          setDomCountry(userResponse.data.home_country);
          setDomRegion(userResponse.data.home_region);
          setDomProvince(userResponse.data.home_province);
          setDomCity(userResponse.data.home_city);
          setDomCityName(userResponse.data.home_city_name);
          setDomRegionName(userResponse.data.home_region_name);

          setIsLoading(false);
        })
      )
      .catch((errors) => {
        setIsLoading(false);

        // userResponse Errors
        if (isAxiosError(errors)) {
          HttpErrorHandler(errors, setErrorMessage);
        }
      });
  };

  // Get Gender Name
  const getDisplayableGender = (gender) => {
    let genderName;

    if (gender === 'male') {
      genderName = 'Uomo';
    } else if (gender === 'female') {
      genderName = 'Donna';
    } else if (gender === 'other') {
      genderName = 'Altro';
    } else {
      genderName = '';
    }

    return setGender({ gender: genderName });
  };

  const getGenderDBName = (name) => {
    let gender;

    if (name === 'uomo') {
      gender = 'male';
    } else if (name === 'donna') {
      gender = 'female';
    } else if (name === 'altro') {
      gender = 'other';
    } else {
      gender = null;
    }

    return gender;
  };

  const getRolesList = () => {
    if (rolesList.length === 0) {
      API.get('/roles')
        .then((response) => {
          setRolesList(response.data.roles);
        })
        .catch((error) => {
          HttpErrorHandler(error, setErrorMessage);
        });
    }
  };

  const getBirthPlacesList = () => {
    if (birthPlacesList.length === 0) {
      API.get('/citiesfiscalcode')
        .then((response) => {
          setBirthPlacesList(response.data);
        })
        .catch((error) => {
          HttpErrorHandler(error, setErrorMessage);
        });
    }
  };

  // Filter positions by internal & external
  useEffect(() => {
    const internalPositions = [];
    const externalPositions = [];

    positions.map((position) => {
      if (position.internal === 1) {
        internalPositions.push(position);
      } else {
        externalPositions.push(position);
      }

      return null;
    });

    setIntPositions(internalPositions);
    setExtPositions(externalPositions);
  }, [positions]);

  // Set displayed positions Tabs
  useEffect(() => {
    if (positionTabValue === 0) {
      setDisplayedPositions(extPositions);
    } else {
      setDisplayedPositions(intPositions);
    }
  }, [positionTabValue, intPositions, extPositions]);

  /* ---------------------------- Update User Data ---------------------------- */
  const [edit, setEdit] = useState(false);

  // Change Birth Date
  const handleBirthDateChange = (date) => {
    const formattedDate = dayjs(date).format('MM/DD/YYYY');
    setBirthDate(formattedDate);
  };

  // Change Dom Is Res
  const handleSwitchChange = () => {
    setDomIsRes(!domIsRes);
  };

  // close edit user data
  const handleCloseEditButtonClick = () => {
    getUserInfo();
    setEdit(!edit);
  };

  // submit update user data
  const updateUserData = (e) => {
    e.preventDefault();
    getRolesList();
    getBirthPlacesList();
    setEdit(true);

    if (edit) {
      // Res Ids

      const CityID = resCity === null ? null : resCity.id ? resCity.id : resCity === '' ? userInfo.res_city_id : null;

      const _resCityName = typeof resCityName === 'string' ? resCityName : null;

      const ProvinceID =
        resProvince === null
          ? null
          : resProvince.id
          ? resProvince.id
          : resProvince === ''
          ? userInfo.res_province_id
          : null;

      const _resRegionName = typeof resRegionName === 'string' ? resRegionName : null;

      const CountryID =
        resCountry === null ? null : resCountry.id ? resCountry.id : resCountry === '' ? userInfo.res_country_id : null;

      // Dom Ids
      const domCityID = domIsRes
        ? CityID
        : !domIsRes && typeof domCity.id === 'string' && domCity.id !== ''
        ? domCity.id
        : !domIsRes && typeof domCity.id === 'string' && domCity.id === ''
        ? null
        : null;

      const _domCityName = typeof domCityName === 'string' ? domCityName : null;

      const domProvinceID = domIsRes
        ? ProvinceID
        : !domIsRes && domProvince && typeof domProvince.id === 'string' && domProvince.id !== ''
        ? domProvince.id
        : !domIsRes && domProvince && typeof domProvince.id === 'string' && domProvince.id === ''
        ? null
        : null;

      const _domRegionName = typeof domRegionName === 'string' ? domRegionName : null;

      const domCountryID = domIsRes
        ? CountryID
        : !domIsRes && domCountry && typeof domCountry.id === 'string' && domCountry.id !== ''
        ? domCountry.id
        : !domIsRes && domCountry && typeof domCountry.id === 'string' && domCountry.id === ''
        ? null
        : null;

      const domStreetValue = domIsRes ? resStreet : domStreet;
      const domZipCodeValue = domIsRes ? resZipCode : domZipCode;

      const formattedData = dayjs(birthDate).format('YYYY/MM/DD');

      const genderName = getGenderDBName(gender && gender.gender ? gender.gender.toLowerCase() : '');

      const updatedData = {
        name,
        surname,
        fiscal_code: fiscalCode.toUpperCase(),
        email: email && email.length > 0 ? email.replace(/\s+/g, '') : '',
        name_alias: aliasName,
        gender: genderName,
        birth_date: formattedData,
        place_of_birth: birthPlace?.id,
        phone: phoneNumber,
        call_code: callCode || '',
        res_equal_home: Number(domIsRes),
        res_country_id: CountryID,
        res_street: resStreet,
        res_zip_code: resZipCode,
        home_country_id: domCountryID,
        home_street: domStreetValue,
        home_zip_code: domZipCodeValue,
        user_online: userInfo.user_online,
        entity_id: userEntity ? userEntity.id : '',
        allow_news: Number(allowNews),
        created_at: userInfo.created_at,
        updated_at: userInfo.updated_at,
        user_roles: userRoles,
      };

      let itemsLeft = false;
      const newErrors = { ...errors };

      Object.keys(updatedData).forEach((key) => {
        const value = updatedData[key];

        if (Object.prototype.hasOwnProperty.call(updatedData, key)) {
          if (
            value === undefined ||
            value === null ||
            value === 'Invalid Date' ||
            (typeof value === 'string' && value.length === 0) ||
            (Array.isArray(value) && value.length === 0)
          ) {

            if (
              (updatedData.user_online || !['phone','call_code', 'email'].includes(key)) &&
              (useAlias || !['name_alias'].includes(key))
            ) {
              itemsLeft = true;
              if (Object.prototype.hasOwnProperty.call(newErrors, key)) {
                newErrors[key] = true;
              }
              handleOpenEditUserDataSnack();
            }
          } else {
            newErrors[key] = false;
          }
        }
      });

      updatedData.res_city_id = typeof _resCityName !== 'string' ? CityID : null;
      updatedData.res_city_name = _resCityName;
      updatedData.res_province_id = typeof _resRegionName !== 'string' ? ProvinceID : null;
      updatedData.res_region_name = _resRegionName;
      updatedData.home_city_id = domIsRes ? updatedData.res_city_id : domCityID;
      updatedData.home_city_name = domIsRes ? _resCityName : _domCityName;
      updatedData.home_province_id = domIsRes ? updatedData.res_province_id : domProvinceID;
      updatedData.home_region_name = domIsRes ? _resRegionName : _domRegionName;
      setErrors(newErrors);

      if (!itemsLeft) {

        API.put(`/users/${userInfo.id}`, updatedData)
          .then((response) => {
            setUserInfo((prevUserInfo) => ({
              ...prevUserInfo,
              member: response.data.user.member,
              verified: response.data.user.verified,
            }));
            setPopUpText('Informazioni salvate');
            showPopup(true);

            getUserInfo();
            setEdit(false);

            const userData = JSON.parse(localStorage.getItem('userData'));

            if (userInfo.id === userData.id) {
              const newUserData = {
                birth_date: updatedData.birth_date,
                id: userInfo.id,
                allow_news: updatedData.allow_news,
                name: updatedData.name,
                roles: userInfo.profiles,
                surname: updatedData.surname,
                name_alias: updatedData.name_alias,
                fiscal_code: updatedData.fiscal_code,
                verified: updatedData.verified,
                card_holder: updatedData.card_holder,
              };

              localStorage.removeItem('userData');
              localStorage.setItem('userData', JSON.stringify(newUserData));
            }
          })
          .catch((error) => {
            HttpErrorHandler(error, setPopUpText);
            showPopup(false);
          });
      }
    } else {
      callEditApis();
    }
  };

  /* ----------------------------------- OTP ---------------------------------- */
  const [emailMenuAnchor, setEmailMenuAnchor] = useState(null);
  const [phoneMenuAnchor, setPhoneMenuAnchor] = useState(null);

  const openEmailMenu = Boolean(emailMenuAnchor);
  const openPhoneMenu = Boolean(phoneMenuAnchor);

  const handleClickPhoneMenu = (event) => {
    setPhoneMenuAnchor(event.currentTarget);
  };

  const handleClosePhoneMenu = () => {
    setPhoneMenuAnchor(null);
  };

  const [openVerifyPhoneDialog, setOpenVerifyPhoneDialog] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState(false);
  const [isUserVerifyingPhone, setIsUserVerifyingPhone] = useState(false);
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');

  // Open Verify Phone Dialog
  const handleOpenVerifyPhoneDialog = () => {
    setIsDialogOpen('phone');
    setOpenVerifyPhoneDialog(true);
    handleClosePhoneMenu();
  };

  // Close Verify Phone Dialog
  const handleCloseVerifyPhoneDialog = () => {
    setOpenVerifyPhoneDialog(false);
    setIsDialogOpen('noDialog');
    setIsUserVerifyingPhone(false);
    setOpen(false);
  };

  const sendOtpSms = () => {
    setIsUserVerifyingPhone(true);

    const countryCode = callCode.slice(1, callCode.length);
    API.patch(`/otp/send_sms?phone=${phoneNumber}&country_code=${countryCode}`)
      .then((response) => {
        setPopUpText('SMS inviato con successo');
        showPopup(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const verifyUserPhone = () => {
    const data = {
      verified_phone: 1,
    };

    API.patch(`/users/${userInfo.id}/verify_phone`, data)
      .then((response) => {
        getUserInfo();
        handleCloseVerifyPhoneDialog();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const handleClickEmailMenu = (event) => {
    setEmailMenuAnchor(event.currentTarget);
  };

  const handleCloseEmailMenu = () => {
    setEmailMenuAnchor(null);
  };

  const [openVerifyEmailDialog, setOpenVerifyEmailDialog] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState(false);
  const [isUserVerifyingEmail, setIsUserVerifyingEmail] = useState(false);
  const [emailVerificationCode, setEmailVerificationCode] = useState('');

  // Open Verify Email Dialog
  const handleOpenVerifyEmailDialog = () => {
    setIsDialogOpen('email');
    setOpenVerifyEmailDialog(true);
    handleCloseEmailMenu();
  };

  // Close Verify Email Dialog
  const handleCloseVerifyEmailDialog = () => {
    setOpenVerifyEmailDialog(false);
    setIsDialogOpen('noDialog');
    setIsUserVerifyingEmail(false);
    setOpen(false);
  };

  const sendOtpEmail = () => {
    setIsUserVerifyingEmail(true);

    API.patch(`/otp/send_mail`, { email: `${email}` })
      .then((response) => {
        setPopUpText('Email inviata con successo');
        showPopup(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const verifyUserEmail = () => {
    const data = {
      verified_email: 1,
    };

    API.patch(`/users/${userInfo.id}/verify_email`, data)
      .then((response) => {
        handleCloseVerifyEmailDialog();
        getUserInfo();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // Get api calls for countries, regions, provinces, cities and entities
  const callEditApis = async () => {
    if (resCountriesList.length === 0 || domCountriesList.length === 0 || EntitiesList.length === 0) {
      setIsEditApisRunning(true);
      try {
        const responses = await axios.all([API.get('/countries')]);

        const [countriesResponse] = responses;

        setResCountriesList(countriesResponse.data);
        setDomCountriesList(countriesResponse.data);

        const italyData = countriesResponse.data.find((element) => element.name === 'Italia');
        setItaly(italyData);

        setIsEditApisRunning(false);
      } catch (error) {
        setIsEditApisRunning(false);
      }
    }
  };

  useEffect(() => {
    if (edit && resCountry && resCountry?.name !== italy?.name) {
      setResProvince('');
      setResRegion('');
      setResCity('');
    }

    if (resCountry?.name !== italy?.name) {
      setResRegionList([]);
      setResProvincesList([]);
      setResCitiesList([]);
    }
  }, [resCountry]);

  useEffect(() => {
    if (edit && domCountry && domCountry?.name !== italy?.name) {
      setDomProvince('');
      setDomRegion('');
      setDomCity('');
    }

    if (domCountry?.name !== italy?.name) {
      setDomRegionList([]);
      setDomProvincesList([]);
      setDomCitiesList([]);
    }
  }, [domCountry]);

  const [openEntitiesFilter, setOpenEntitiesFilter] = useState(false);

  const handleCloseEntitiesFilterDialog = () => {
    setOpenEntitiesFilter(false);
    setOpen(false);
  };

  const [selectedEntity, setSelectedEntity] = useState(null);

  const getSelectedEntity = (selectedEntity) => {
    setSelectedEntity(selectedEntity);
  };

  useEffect(() => {
    if (selectedEntity) {
      setUserEntity(selectedEntity);
    }
  }, [selectedEntity]);

  // Get resRegionList
  useEffect(() => {
    if (edit) {
      if (resCountry !== null && resCountry !== undefined && resCountry !== '') {
        if (typeof resCountry === 'object' && Object.keys(resCountry).length > 0 && resCountry?.name === italy.name) {
          API.get(`/countries/regions`, {
            params: {
              id: resCountry.id,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setResRegionList(response.data);

              if (domIsRes) {
                setDomRegionList(response.data);
              }
            } else {
              setResRegionList([]);
              setResProvincesList([]);
              setResCitiesList([]);
            }
          });
        }
      }
    }
  }, [resCountry, edit]);

  // Get resProvincesList
  useEffect(() => {
    if (edit) {
      if (resRegion !== null && resRegion !== undefined && resRegion !== '') {
        if (typeof resRegion === 'object' && Object.keys(resRegion).length > 0 && resCountry?.name === italy.name) {
          API.get(`/regions/provinces`, {
            params: {
              region_name: resRegion.region_name,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setResProvincesList(response.data);

              if (domIsRes) {
                setDomProvincesList(response.data);
              }
            } else {
              setResProvincesList([]);
              setResCitiesList([]);
            }
          });
        }
      }
    }
  }, [resRegion, edit]);

  // Get resCitiesList
  useEffect(() => {
    if (edit) {
      if (resProvince !== null && resProvince !== undefined && resProvince !== '') {
        if (typeof resProvince === 'object' && Object.keys(resProvince).length > 0) {
          API.get(`/provinces/cities`, {
            params: {
              id: resProvince.id,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setResCitiesList(response.data);
              if (domIsRes) {
                setDomCitiesList(response.data);
              }
            } else {
              setResCitiesList([]);
            }
          });
        }
      }
    }
  }, [resProvince, edit]);

  // Get domRegionList
  useEffect(() => {
    if (edit && !domIsRes) {
      if (domCountry !== null && domCountry !== undefined && domCountry !== '') {
        if (typeof domCountry === 'object' && Object.keys(domCountry).length > 0 && domCountry?.name === italy.name) {
          API.get(`/countries/regions`, {
            params: {
              id: domCountry.id,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setDomRegionList(response.data);
            } else {
              setDomRegionList([]);
              setDomProvincesList([]);
              setDomCitiesList([]);
            }
          });
        }
      }
    }
  }, [domCountry, edit]);

  // Get domProvincesList
  useEffect(() => {
    if (edit && !domIsRes) {
      if (domRegion !== null && domRegion !== undefined && domRegion !== '') {
        if (typeof domRegion === 'object' && Object.keys(domRegion).length > 0 && domCountry?.name === italy.name) {
          API.get(`/regions/provinces`, {
            params: {
              region_name: domRegion.region_name,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setDomProvincesList(response.data);
            } else {
              setDomProvincesList([]);
              setDomCitiesList([]);
            }
          });
        }
      }
    }
  }, [domRegion, edit]);

  // Get domCitiesList
  useEffect(() => {
    if (edit && !domIsRes) {
      if (domProvince !== null && domProvince !== undefined && domProvince !== '') {
        if (typeof domProvince === 'object' && Object.keys(domProvince).length > 0) {
          API.get(`/provinces/cities`, {
            params: {
              id: domProvince.id,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setDomCitiesList(response.data);
            } else {
              setDomCitiesList([]);
            }
          });
        }
      }
    }
  }, [domProvince, edit]);

  // Filter Entities List
  useEffect(() => {
    const filteredEntities = [];

    EntitiesList.map((entity) => {
      if (entity.level === 3 && entity.branch === 'NAZ') {
        filteredEntities.push(entity);
      }
      return null;
    });
  }, [EntitiesList]);

  // newsletter subscription dialog
  const [openNewsletterDialog, setOpenNewsletterDialog] = useState(false);

  // open newsletter subscription dialog
  const onOpenNewsletterDialog = () => {
    setOpenNewsletterDialog(true);
  };

  // close newsletter subscription dialog
  const onCloseNewsletterDialog = () => {
    setOpenNewsletterDialog(false);
    setOpen(false);
  };

  // change newsletter permissions
  const changeNewsletterPermission = () => {
    API.patch(`users/${userID}/newsletters`, { allow_news: Number(!allowNews) })
      .then(() => {
        onCloseNewsletterDialog();
        getUserInfo();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // lock user dialog
  const [openLockedUserDialog, setOpenLockedUserDialog] = useState(false);

  // open lock user dialog
  const onOpenLockedUserDialog = () => {
    setOpenLockedUserDialog(true);
  };

  // close lock user dialog
  const onCloseLockedUserDialog = () => {
    setOpenLockedUserDialog(false);
    setOpen(false);
  };

  // lock user
  const handleLockUser = () => {
    API.patch(`admin/users/${userID}/block`, { reason: 'Bloccato da amministratore' })
      .then(() => {
        onCloseLockedUserDialog();
        setLockedUser(!lockedUser);
        getUserInfo();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // unlock user
  const handleUnlockUser = () => {
    API.patch(`admin/users/${userID}/unblock`)
      .then(() => {
        onCloseLockedUserDialog();
        setLockedUser(!lockedUser);
        getUserInfo();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // verify user
  const [openVerifyUserDialog, setOpenVerifyUserDialog] = useState(false);

  // open verify user dialog
  const onOpenVerifyUserDialog = () => {
    setOpenVerifyUserDialog(true);
  };

  // close verify user dialog
  const onCloseVerifyUserDialog = () => {
    setOpenVerifyUserDialog(false);
    setOpen(false);
  };

  // change verified user value
  const changeVerifiedUserValue = () => {
    API.patch(`/users/${userID}/verified?val=${verifiedUser ? 0 : 1}`)
      .then((response) => {
        onCloseVerifyUserDialog();
        setVerifiedUser(!verifiedUser);
        getUserInfo();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const [openOnlineUserDialog, setOpenOnlineUserDialog] = useState(false);

  const onOpenOnlineUserDialog = () => {
    setOpenOnlineUserDialog(true);
  };

  const onCloseOnlineUserDialog = () => {
    setOpenOnlineUserDialog(false);
    setOpen(false);
  };

  const changeOnlineUserValue = () => {
    API.patch(`/users/${userID}/online?online=${onlineUser ? 0 : 1}`)
      .then((response) => {
        onCloseOnlineUserDialog();
        setOnlineUser(!onlineUser);
        getUserInfo();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* ----------------------------- Reset Password ----------------------------- */
  const [openResetPassword, setOpenResetPassword] = useState(false);

  // Open Reset Password Dialog
  const handleOpenResetPassword = () => {
    setIsDialogOpen('resetPassword');
    setOpenResetPassword(true);
    setOpen(false);
  };

  // Close Reset Password Dialog
  const handleCloseResetPassword = () => {
    setOpenResetPassword(false);
    setIsDialogOpen('noDialog');
    setOpen(false);
  };

  // submit reset password
  const handleResetPassword = (e) => {
    e.preventDefault();

    const userFiscalCode = {
      fiscal_code: userInfo.fiscal_code,
    };

    API.patch(`/passwordforgotten`, userFiscalCode)
      .then((response) => {
        setPopUpText('Email di reset inviata correttamente');
        showPopup(true);
        setTimeout(() => {
          handleCloseResetPassword();
        }, POPUP_DURATION_DELAY);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* ----------------------------- Authorization ----------------------------- */
  // Auth
  const [authApiCalled, setAuthApiCalled] = useState(false);
  const [openNewAuthorization, setOpenNewAuthorization] = useState(false);
  const [openShowAuthorization, setOpenShowAuthorization] = useState(false);
  const [openDeleteAuthorization, setOpenDeleteAuthorization] = useState(false);
  const [authorizationList, setAuthorizationList] = useState([]);
  const [authorizationToShow, setAuthorizationToShow] = useState({});
  const [openNewAuthEntitiesFilter, setOpenNewAuthEntitiesFilter] = useState(false);
  const [newAuthEntity, setNewAuthEntity] = useState(null);
  const [newAuthObject, setNewAuthObject] = useState({});
  const [newAuthType, setNewAuthType] = useState('');
  const [authorizationToDelete, setAuthorizationToDelete] = useState({});

  const [authTypeListCalled, setAuthTypeListCalled] = useState(false);
  const [authTypeList, setAuthTypeList] = useState([]);
  const [authObjListCalled, setAuthObjListCalled] = useState(false);
  const [authObjList, setAuthObjList] = useState([]);
  const [authDataCalled, setAuthDataCalled] = useState(false);

  // authorization table pagination
  const [authPage, setAuthPage] = useState(0);
  const [authRowsPerPage, setAuthRowsPerPage] = useState(is600Screen ? 10 : 5);

  const emptyAuthRows = authPage > 0 ? Math.max(0, (1 + authPage) * authRowsPerPage - authorizationList.length) : 0;

  // change auth page
  const handleAuthPage = (event, newPage) => {
    setAuthPage(newPage);
  };

  // change auth rows per page
  const handleAuthRowsPerPage = (event) => {
    setAuthRowsPerPage(parseInt(event.target.value, 10));
    setAuthPage(0);
  };

  // Call Authorization Api
  useEffect(() => {
    if (value === 4 && !authApiCalled) {
      getAuthorizationList();
    }
  }, [value, authApiCalled]);

  // Get authorizationList
  const getAuthorizationList = () => {
    API.get(`/userauth/user/${userID}`).then((response) => {
      setAuthApiCalled(true);
      setAuthorizationList(response.data);
    });
  };

  /* --------------------------- Show Authorization --------------------------- */
  const handleOpenShowAuthorizationDialog = (auth) => {
    setOpenShowAuthorization(true);
    setAuthorizationToShow(auth);
  };

  const handleCloseShowAuthorizationDialog = () => {
    setOpenShowAuthorization(false);
    setAuthorizationToShow({});
    setOpen(false);
  };

  /* -------------------------- Delete Authorization -------------------------- */
  const handleOpenDeleteAuthorizationDialog = (auth) => {
    setIsDialogOpen('deleteAuth');
    setOpenDeleteAuthorization(true);
    setAuthorizationToDelete(auth);
  };

  const handleCloseDeleteAuthorizationDialog = () => {
    setIsDialogOpen('noDialog');

    setOpenDeleteAuthorization(false);
    setAuthorizationToDelete({});
  };

  const handleDeleteAuthorization = () => {
    API.delete(`/userauth/${authorizationToDelete.id}`)
      .then((response) => {
        handleCloseDeleteAuthorizationDialog();
        getAuthorizationList();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* ---------------------------- New Authorization --------------------------- */
  const handleOpenNewAuthorizationDialog = () => {
    setIsDialogOpen('newAuth');
    setOpenNewAuthorization(true);
    getNewAuthorizationData();
  };

  // Get authTypeList & authObjectList
  const getNewAuthorizationData = () => {
    if (!authTypeListCalled) {
      API.get(`/authtypelist`).then((response) => {
        setAuthTypeList(response.data);
        setAuthTypeListCalled(true);
      });
    }

    if (!authObjListCalled) {
      API.get(`/authobjectlist`).then((response) => {
        setAuthObjList(response.data);
        setAuthObjListCalled(true);
      });
    }
  };

  // Set all auth apis called
  useEffect(() => {
    if (authTypeListCalled && authObjListCalled) {
      setAuthDataCalled(true);
    }
  }, [authTypeListCalled, authObjListCalled]);

  // Close new auth dialog
  const handleCloseNewAuthorizationDialog = () => {
    setIsDialogOpen('noDialog');
    setOpenNewAuthorization(false);
    setOpen(false);

    setNewAuthEntity(null);
    setNewAuthObject({});
    setNewAuthType('');
  };

  // Close new auth entity dialog
  const handleCloseNewAuthEntitiesFilterDialog = () => {
    setOpenNewAuthEntitiesFilter(false);
  };

  // Get new auth Entity
  const getNewAuthEntity = (selectedEntity) => {
    setNewAuthEntity(selectedEntity);
  };

  const handleCheckboxChange = (event, authType) => {
    const isChecked = event.target.checked;
    const type = authType.type;

    setNewAuthType((prev) => {
      if (isChecked) {
        return prev.includes(type) ? prev : prev + type;
      }
      return prev.replace(type, '');
    });
  };

  const getAuthTypeCheckboxLabel = (type) => {
    switch (type) {
      case 'R':
        return 'Lettura';
      case 'W':
        return 'Scrittura';
      case 'D':
        return 'Cancellazione';
      default:
        return '';
    }
  };

  const onAddAuthorization = (e) => {
    e.preventDefault();

    const data = {
      user_id: userInfo.id,
      entity_id: newAuthEntity ? newAuthEntity.id : '',
      object_id: newAuthObject ? newAuthObject.id : '',
      authorization_type: newAuthType,
    };

    let itemsLeft = false;
    Object.keys(data).forEach((key) => {
      const value = data[key];

      if (Object.prototype.hasOwnProperty.call(data, key)) {
        if (
          value === undefined ||
          value === null ||
          value === 'Invalid Date' ||
          (typeof value === 'string' && value.length === 0)
        ) {
          itemsLeft = true;
          handleOpenNewAuthDataSnack();
        }
      }
    });

    if (!itemsLeft) {
      API.post(`/userauth`, data)
        .then((response) => {
          getAuthorizationList();
          handleCloseNewAuthorizationDialog();
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }
  };

  /* ------------------------------- New Position ------------------------------- */
  const [openNewPosition, setOpenNewPosition] = useState(false);

  // Open NewPosition Dialog
  const handleOpenNewPosition = () => {
    setOpenNewPosition(true);
  };

  // Close NewPosition Dialog
  const handleCloseNewPosition = () => {
    setOpenNewPosition(false);
    setOpen(false);
  };

  // Get User Positions
  const getUserPositions = () => {
    API.get(`/users/${userInfo.id}`)
      .then((response) => {
        setPositions(response.data.positions);
        setActiveRatesApiCalled(false);
      })
      .catch((error) => {
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  /* ------------------------------- Edit Position ------------------------------ */
  const [openEditPosition, setOpenEditPosition] = useState(false);
  const [positionToEdit, setPositionToEdit] = useState('');

  // Open Edit Position Dialog
  const handleOpenEditPosition = (position) => {
    setOpenEditPosition(true);
    setPositionToEdit(position);
  };

  // Close Edit Position Dialog
  const handleCloseEditPosition = () => {
    setOpenEditPosition(false);
    setOpen(false);
  };

  /* ------------------------------ Charge Delete ----------------------------- */
  const [openDeleteCharge, setOpenDeleteCharge] = useState(false);

  // open delete position dialog
  const onOpenDeleteChargeDialog = (position) => {
    setPositionToEdit(position);
    setOpenDeleteCharge(true);
  };

  // close delete position dialog
  const onCloseDeleteChargeDialog = () => {
    setPositionToEdit('');
    setOpenDeleteCharge(false);
  };

  // delete position
  const onDeleteCharge = (event) => {
    event.preventDefault();

    API.delete(`/charges/${positionToEdit.id}`)
      .then((response) => {
        getUserPositions();
        onCloseDeleteChargeDialog();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* ----------------------------- Create New Card ---------------------------- */
  const [openNewCard, setOpenNewCard] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState({});
  const [paymentTypes, setPaymentTypes] = useState({});

  const [statusApiCalled, setStatusApiCalled] = useState(false);
  const [typesApiCalled, setTypesApiCalled] = useState(false);

  // Open New Card Modal
  const handleOpenNewCard = () => {
    setIsDialogOpen('createCard');
    getActiveCampaign();
    getPaymentStatus();
    getPaymentTypes();
    setOpenNewCard(true);
  };

  // Close New Card Modal
  const handleCloseNewCard = () => {
    setOpenNewCard(false);
    setIsDialogOpen('noDialog');
    setOpen(false);
  };

  // get payment status data
  const getPaymentStatus = () => {
    if (Object.keys(paymentStatus).length === 0) {
      API.get('/paystatus')
        .then((response) => {
          setPaymentStatus(response.data);
          setStatusApiCalled(true);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }
  };

  // Get PaymentTypes API
  const getPaymentTypes = () => {
    if (Object.keys(paymentTypes).length === 0) {
      if (canManageUser) {
        API.get('/paytypes').then((response) => {
          setPaymentTypes(response.data.payment_types);
          setTypesApiCalled(true);
        });
      } else {
        API.get('/paytypes?for_admin=1').then((response) => {
          setPaymentTypes(response.data.payment_types);
          setTypesApiCalled(true);
        });
      }
    }
  };

  // create user new card
  const onCreateMembershipCard = (e) => {
    e.preventDefault();

    const cardRates = {
      user_id: userInfo.id,
      campaign_id: userRates[0].campaign_id,
      rates: [],
      payment_entity_id: userEntity.id,
      payment_online: 1,
      digital_format: onlineMembershipCard,
    };

    paymentStatus.forEach((status) => {
      if (status.type === 'W') {
        cardRates.payment_status = status.id;
      }
    });

    userRates.forEach((element) => {
      if (element.price !== 0 && element.price !== 0.0 && element.price !== '0.00') {
        cardRates.rates.push({
          id: element.id,
          price: isGdAvailable ? Number(element.price_discounted) : Number(element.price),
        });
      }
    });

    if (cardRates.rates.length !== 0) {
      API.post('/memcards', cardRates)
        .then((response) => {
          handleCloseNewCard();
          getUserCards();
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    } else {
      setSnackbarData({ text: "L'importo non può essere zero", color: 'warning' });
      setOpenSnackbar(true);

      setTimeout(() => {
        setOpenSnackbar(false);
      }, POPUP_DURATION_DELAY);
    }
  };

  // Get User Cards
  const getUserCards = () => {
    API.get(`/users/${userInfo.id}`)
      .then((response) => {
        setCards(response.data.cards);
      })
      .catch((error) => {
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  // disable rates input
  const handleDisabledInput = (index) => {
    const newDisabledStates = [...disabledStates];
    newDisabledStates[index] = !newDisabledStates[index];
    setDisabledStates(newDisabledStates);
  };
  const [otherValue, setOtherValue] = useState({});
  const isSaveEnabled = !Number.isNaN(totalPrice) && totalPrice > 0;

  // change modifiable rates value
  const handleRateChange = (index, newValue) => {
    setUserRates((prevRates) => {
      const newRates = [...prevRates];
      newRates[index].price = Number(newValue).toFixed(2).toString();
      newRates[index].price_discounted = Number(newValue).toFixed(2).toString();
      return newRates;
    });
  };

  // disable new card
  const [disableNewCard, setDisableNewCard] = useState(true);
  useEffect(() => {
    const foundCard = cards.some((card) => dayjs(card.end_date) > dayjs());

    if (foundCard) {
      setDisableNewCard(true);
    } else {
      setDisableNewCard(false);
    }
  }, [cards]);

  /* --------------------------- Create Expired Card -------------------------- */
  const [openNewExpiredCard, setOpenNewExpiredCard] = useState(false);
  const [displayExpiredOptions, setDisplayExpiredOptions] = useState(true);
  const [expiredCampaigns, setExpiredCampaigns] = useState([]);
  const [selectedExpiredCampaign, setSelectedExpiredCampaign] = useState({});
  const [expiredRates, setExpiredRates] = useState([]);
  const [disabledExpiredStates, setDisabledExpiredStates] = useState(expiredRates.map(() => false));
  const [otherExpiredValue, setOtherExpiredValue] = useState({});
  const [expiredRatesApiCalled, setExpiredRatesApiCalled] = useState(false);
  const [expiredRatesTotalPrice, setExpiredRatesTotalPrice] = useState(0);

  const handleDisabledExpiredInput = (index) => {
    const newDisabledExpiredStates = [...disabledExpiredStates];
    newDisabledExpiredStates[index] = !newDisabledExpiredStates[index];
    setDisabledExpiredStates(newDisabledExpiredStates);
  };

  // change modifiable rates value
  const handleExpiredRateChange = (index, newValue) => {
    setExpiredRates((prevRates) => {
      const newRates = [...prevRates];
      newRates[index].price = Number(newValue).toFixed(2).toString();
      newRates[index].price_discounted = Number(newValue).toFixed(2).toString();
      return newRates;
    });
  };

  const handleOpenNewExpiredCard = () => {
    setIsDialogOpen('createExpiredCard');
    setOpenNewExpiredCard(true);
    getExpiredCampaigns();
  };

  const handleCloseNewExpiredCard = () => {
    setIsDialogOpen('noDialog');
    setOpenNewExpiredCard(false);
    setDisplayExpiredOptions(true);
    setExpiredRates([]);
    setSelectedExpiredCampaign({});
    setExpiredRatesTotalPrice(0);
    setExpiredRatesApiCalled(false);
    setOpen(false);
  };

  const handleSelectedExpiredCampaign = () => {
    if (Object.keys(selectedExpiredCampaign).length > 0) {
      setDisplayExpiredOptions(false);
      getActiveRates(selectedExpiredCampaign.id);
      getPaymentStatus();
      getPaymentTypes();
    } else {
      setPopUpText('Per proseguire è necessario selezionare una campagna');
      showPopup(false);
    }
  };

  const getExpiredCampaigns = () => {
    if (expiredCampaigns.length === 0) {
      API.get('/expired_campaigns')
        .then((response) => {
          setExpiredCampaigns(response.data);
        })
        .catch((error) => {
          HttpErrorHandler(error, setExpiredSubscriptionApiError);
        });
    }
  };

  useEffect(() => {
    let ratesSum;

    if (isGdAvailable) {
      ratesSum = expiredRates.reduce((sum, rate) => sum + Number(rate.price_discounted), 0);
    } else {
      ratesSum = expiredRates.reduce((sum, rate) => sum + Number(rate.price), 0);
    }
    setExpiredRatesTotalPrice(ratesSum);
  }, [expiredRates]);

  const handleCreateExpiredCard = (e) => {
    e.preventDefault();

    const data = {
      user_id: userInfo.id,
      campaign_id: selectedExpiredCampaign.id,
      rates: [],
      payment_entity_id: userEntity.id,
      payment_online: 1,
      digital_format: true,
    };

    expiredRates.forEach((element) => {
      if (element.price !== 0 && element.price !== 0.0 && element.price !== '0.00') {
        data.rates.push({
          id: element.id,
          price: isGdAvailable ? Number(element.price_discounted) : Number(element.price),
        });
      }
    });

    paymentStatus.forEach((status) => {
      if (status.type === 'C') {
        data.payment_status = status.id;
      }
    });

    paymentTypes.forEach((type) => {
      if (type.type === 'CS') {
        data.payment_type = type.id;
      }
    });

    API.post('/memcards', data)
      .then((response) => {
        handleCloseNewExpiredCard();
        getUserCards();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* -------------------------------- Edit Card ------------------------------- */
  const [openEditCard, setOpenEditCard] = useState(false);
  const [cardToEdit, setCardToEdit] = useState({});
  const [cardToEditPaymentTypeType, setCardToEditPaymentTypeType] = useState('');
  const [cardToEditPaymentType, setCardToEditPaymentType] = useState({});
  const [cardToEditPaymentStatusType, setCardToEditPaymentStatusType] = useState('');
  const [cardToEditPaymentStatus, setCardToEditPaymentStatus] = useState({});
  const [cardToEditAuthCode, setCardToEditAuthCode] = useState('');
  const [cardToEditTransactionID, setCardToEditTransactionID] = useState('');
  const [cardToEditTerminalCode, setCardToEditTerminalCode] = useState('');
  const [onlineMembershipCard, setOnlineMembershipCard] = useState(true);

  useEffect(() => {
    if (cardToEdit && Object.keys(cardToEdit).length > 0) {
      let canEditPaymentInfo = false;

      if (!canManageUser) {
        if (
          cardToEditPaymentTypeType !== 'C' ||
          (cardToEditPaymentTypeType === 'C' && cardToEditPaymentStatusType === 'P')
        ) {
          canEditPaymentInfo = true;
        }
      }
      setApplyPaymentStatusFilter(canEditPaymentInfo);
    }
  }, [cardToEdit, cardToEditPaymentStatusType, cardToEditPaymentTypeType]);

  // Open Edit Card Dialog
  const handleOpenEditCard = (card) => {
    getPaymentStatus();
    getPaymentTypes();

    setIsDialogOpen('editCard');
    setOpenEditCard(true);
    setCardToEdit(card);
    setCardToEditPaymentStatusType(card.payment_status.type);

    if (card.payment_pos_term_id) {
      setCardToEditTerminalCode(card.payment_pos_term_id);
    }

    if (card.payment_pos_auth_code) {
      setCardToEditAuthCode(card.payment_pos_auth_code);
    }

    if (card.payment_pp_trans_id) {
      setCardToEditTransactionID(card.payment_pp_trans_id);
    }

    if (card.payment_type) {
      setCardToEditPaymentTypeType(card.payment_type.type);
    }
  };

  // Close Edit Card Dialog
  const handleCloseEditCard = () => {
    setOpenEditCard(false);
    setIsDialogOpen('noDialog');
    setCardToEdit({});
    setOpen(false);
  };

  // Find card to edit payment status
  useEffect(() => {
    if (statusApiCalled) {
      let foundPaymentStatus;

      if (cardToEdit && Object.keys(cardToEdit).length > 0) {
        foundPaymentStatus = paymentStatus.find(
          (payment) => payment.type.toLowerCase() === cardToEditPaymentStatusType?.toLocaleLowerCase()
        );
      }

      setCardToEditPaymentStatus(foundPaymentStatus);
    }
  }, [cardToEdit, cardToEditPaymentStatusType, statusApiCalled]);

  // Find card to edit payment type
  useEffect(() => {
    if (typesApiCalled) {
      let foundPaymentType;

      if (cardToEdit && Object.keys(cardToEdit).length > 0) {
        foundPaymentType = paymentTypes.find(
          (payment) => payment.type.toLowerCase() === cardToEditPaymentTypeType.toLowerCase()
        );
      }

      setCardToEditPaymentType(foundPaymentType);
    }
  }, [cardToEdit, cardToEditPaymentTypeType, typesApiCalled]);

  // Edit Card Info
  const handleEditCard = (e) => {
    e.preventDefault();

    const updatedCardData = {
      user_id: userInfo.id,
      campaign_id: cardToEdit.campaign_id,
      payment_status: cardToEditPaymentStatus.id,
      payment_type: cardToEditPaymentType.id,
      payment_entity_id: userInfo.entity_id,
      payment_pos_auth_code: cardToEditAuthCode,
      payment_pos_term_id: cardToEditTerminalCode,
      payment_online: 1,
    };

    API.put(`/memcards/${cardToEdit.card_id}`, updatedCardData)
      .then((response) => {
        getUserInfo();
        setTimeout(() => {
          handleCloseEditCard();
        }, POPUP_DURATION_DELAY);
        setPopUpText('Informazioni salvate');
        showPopup(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const sendBankDataEmail = (e) => {
    e.preventDefault();

    const data = {
      id: cardToEdit.card_id,
      is_admin: 1,
    };

    API.get('/paymentbank')
      .then(() => {
        API.post(`/paybanksendmail`, data)
          .then(() => {
            showPopup(true, 'Email inviata con successo');
            setTimeout(() => {
              handleCloseEditCard();
            }, POPUP_DURATION_SUCCESS);
          })
          .catch((error) => {
            HttpErrorHandler(error, setPopUpText);
            showPopup(false);
          });
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* ------------------------------- Delete Card ------------------------------ */
  const [openDeleteCard, setOpenDeleteCard] = useState(false);
  const [cardToDelete, setCardToDelete] = useState({});

  const handleOpenDeleteCard = (card) => {
    setIsDialogOpen('deleteCard');
    setOpenDeleteCard(true);
    setCardToDelete(card);
  };

  const handleCloseDeleteCard = () => {
    setIsDialogOpen('noDialog');
    setOpenDeleteCard(false);
    setOpen(false);
    setCardToDelete({});
  };

  const handleDeleteCard = () => {
    API.delete(`/memcards/${cardToDelete.card_id}`)
      .then((response) => {
        getUserInfo();
        setTimeout(() => {
          handleCloseDeleteCard();
        }, POPUP_DURATION_DELAY);
        setPopUpText(response.data.message);
        showPopup(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* -------------------------  Table Pagination ------------------------- */
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(is600Screen ? 10 : 5);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - positions.length) : 0;

  // change page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // change rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  if (!userID) {
    return <Navigate to={{ pathname: '/404' }} />;
  }

  return (
    <div>
      <Helmet>
        <title>Anagrafica Utenti</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ height: '100%' }}>
        {/* Tabs  */}
        <Box width="100%" height="100%">
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              variant={is600Screen ? 'fullWidth' : 'scrollable'}
              allowScrollButtonsMobile
              scrollButtons="auto"
              value={value}
              onChange={handleChangeUserTabs}
              aria-label="user tab panel"
            >
              <Tab
                label="Anagrafica"
                sx={{ minWidth: 'fit-content', flex: 1 }}
                {...a11yProps(0, 'quintuple')}
                icon={<AssignmentIcon />}
                iconPosition="start"
              />
              <Tab
                label="Tesseramento"
                sx={{ minWidth: 'fit-content', flex: 1 }}
                {...a11yProps(1, 'quintuple')}
                icon={<BadgeIcon />}
                iconPosition="start"
              />
              <Tab
                label="Cariche"
                sx={{ minWidth: 'fit-content', flex: 1 }}
                {...a11yProps(2, 'quintuple')}
                icon={<GppGoodIcon />}
                iconPosition="start"
              />
              <Tab
                label="Badge"
                sx={{ minWidth: 'fit-content', flex: 1 }}
                {...a11yProps(3, 'quintuple')}
                icon={<VerifiedIcon />}
                iconPosition="start"
              />
              <Tab
                label="Autorizzazioni"
                sx={{ minWidth: 'fit-content', flex: 1 }}
                {...a11yProps(4, 'quintuple')}
                icon={<AdminPanelSettingsIcon />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* User data tab */}
          <QuintupleTabPanel value={value} index={0}>
            {isLoading ? (
              LoadingUI()
            ) : errorMessage.length === 0 ? (
              <Grid container spacing={3} alignItems="flex-start !important">
                <Grid item xs={12} md={4}>
                  <Grid container spacing={3}>
                    {/* User Card */}
                    <Grid item xs={12}>
                      <Card sx={{ p: '24px' }}>
                        <List>
                          <ListItem sx={{ d: 'flex', justifyContent: 'center' }}>
                            <Avatar sx={{ width: '150px', height: '150px' }} src="/assets/images/img/user.png" />
                          </ListItem>

                          <ListItem sx={{ d: 'flex', justifyContent: 'center', pb: '20px' }}>
                            {userInfo.name_alias === null
                              ? `${userInfo.name} ${userInfo.surname}`
                              : userInfo.name_alias}
                          </ListItem>

                          <ListItem sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Stack direction="column" alignItems="center" justifyContent="center" spacing={1}>
                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                <Typography
                                  sx={{
                                    padding: '4px 10px',
                                    fontSize: '.8em',
                                    backgroundColor: userInfo.verified ? '#dbf6e5' : '#ffe4de',
                                    color: userInfo.verified ? '#118d57' : '#b71d18',
                                    borderRadius: '16px',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                  }}
                                >
                                  {userInfo.verified ? 'Verificato' : 'Non Verificato'}
                                </Typography>

                                <Typography
                                  sx={{
                                    padding: '4px 10px',
                                    fontSize: '.8em',
                                    backgroundColor: userInfo.current_campcard_payed ? '#dbf6e5' : '#ffe4de',
                                    color: userInfo.current_campcard_payed ? '#118d57' : '#b71d18',
                                    borderRadius: '16px',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                  }}
                                >
                                  {userInfo.current_campcard_payed ? 'Tesserato' : 'Non Tesserato'}
                                </Typography>
                              </Stack>

                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                <Typography
                                  sx={{
                                    padding: '4px 10px',
                                    fontSize: '.8em',
                                    backgroundColor: userInfo.user_online ? '#dbf6e5' : '#ffe4de',
                                    color: userInfo.user_online ? '#118d57' : '#b71d18',
                                    borderRadius: '16px',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                  }}
                                >
                                  {userInfo.user_online ? 'Online' : 'Offline'}
                                </Typography>
                                <Typography
                                  sx={{
                                    padding: '4px 10px',
                                    fontSize: '.8em',
                                    backgroundColor: !userInfo.blocked ? '#dbf6e5' : '#ffe4de',
                                    color: !userInfo.blocked ? '#118d57' : '#b71d18',
                                    borderRadius: '16px',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                  }}
                                >
                                  {!userInfo.blocked ? 'Attivo' : 'Bloccato'}
                                </Typography>
                              </Stack>
                            </Stack>
                          </ListItem>
                        </List>
                      </Card>
                    </Grid>

                    {/* manage user statuses card */}
                    <Grid item xs={12}>
                      <Card sx={{ padding: '24px' }}>
                        <CardHeader sx={{ padding: '0' }} title="Gestione utente" />
                        <Stack direction="column" justifyContent="center" alignItems="start">
                          {/* Verify user */}
                          <FormControlLabel
                            sx={{ paddingTop: '12px' }}
                            control={
                              <Switch
                                disabled={!canModifyUser}
                                checked={Boolean(verifiedUser)}
                                onClick={onOpenVerifyUserDialog}
                              />
                            }
                            label={verifiedUser ? 'Utente verificato' : 'Utente non verificato'}
                          />

                          {/* Verify User Dialog */}
                          <DecisionDialog
                            openDialog={openVerifyUserDialog}
                            dialogTitle={`${verifiedUser ? 'Verifica' : 'Rimuovi verifica'} utente`}
                            dialogDescription={`Sei sicuro di voler ${
                              verifiedUser ? "rimuovere la verifica all'" : "verificare l'"
                            } utente?`}
                            closeOperation={onCloseVerifyUserDialog}
                            requestOperation={changeVerifiedUserValue}
                            requestText={verifiedUser ? 'Rimuovi verifica' : 'Verifica'}
                            closeText={'Chiudi'}
                            RequestButton={DefaultContainedButton}
                          />

                          {/* Online user */}
                          <FormControlLabel
                            sx={{ paddingTop: '12px' }}
                            control={
                              <Switch
                                disabled={!canModifyUser}
                                checked={Boolean(onlineUser)}
                                onClick={onOpenOnlineUserDialog}
                              />
                            }
                            label={onlineUser ? 'Utente online' : 'Utente offline'}
                          />

                          {/* Online User Dialog */}
                          <DecisionDialog
                            openDialog={openOnlineUserDialog}
                            dialogTitle={`${onlineUser ? 'Utente Online' : 'Utente Offline'}`}
                            dialogDescription={`Sei sicuro di voler ${
                              onlineUser
                                ? 'impostare come offline il seguente utente?'
                                : 'impostare come online il seguente utente?'
                            }`}
                            closeOperation={onCloseOnlineUserDialog}
                            requestOperation={changeOnlineUserValue}
                            requestText={onlineUser ? 'Imposta Offline' : 'Imposta Online'}
                            closeText={'Chiudi'}
                            RequestButton={DefaultContainedButton}
                          />

                          {/* Lock User */}
                          <FormControlLabel
                            sx={{ paddingTop: '12px' }}
                            control={
                              <Switch
                                disabled={!canModifyUser}
                                checked={Boolean(!lockedUser)}
                                onClick={onOpenLockedUserDialog}
                              />
                            }
                            label={!lockedUser ? 'Utente attivo' : 'Utente bloccato'}
                          />

                          {/* Lock User Dialog */}
                          <DecisionDialog
                            openDialog={openLockedUserDialog}
                            dialogTitle={`${!lockedUser ? 'Blocca' : 'Sblocca'} utente`}
                            dialogDescription={`Sei sicuro di voler ${
                              !lockedUser ? 'bloccare' : 'sbloccare'
                            } l'utente?`}
                            closeOperation={onCloseLockedUserDialog}
                            requestOperation={!lockedUser ? handleLockUser : handleUnlockUser}
                            requestText={!lockedUser ? 'Blocca' : 'Sblocca'}
                            closeText={'Chiudi'}
                            RequestButton={DefaultContainedButton}
                          />

                          {/* Allow Newsletter */}
                          <FormControlLabel
                            sx={{ paddingTop: '12px' }}
                            control={
                              <Switch disabled={!canModifyUser} checked={allowNews} onClick={onOpenNewsletterDialog} />
                            }
                            label="Riceve comunicazioni"
                          />

                          {/* Allow Newsletter Dialog */}
                          <DecisionDialog
                            openDialog={openNewsletterDialog}
                            dialogTitle={`${allowNews ? 'Disattiva' : 'Attiva'} comunicazioni`}
                            dialogDescription={`Sei sicuro di voler ${
                              allowNews ? 'disattivare' : 'attivare'
                            } le comunicazioni?`}
                            closeOperation={onCloseNewsletterDialog}
                            requestOperation={changeNewsletterPermission}
                            requestText={allowNews ? 'Disattiva' : 'Attiva'}
                            closeText={'Chiudi'}
                            RequestButton={DefaultContainedButton}
                          />
                        </Stack>

                        {/* <Stack sx={{paddingTop:'12px'}}>
                          <Typography variant='span' sx={{color:'#919eab'}}>{userInfo.id === userInfo.created_user ? 'Registrazione Autonoma' : `Registrato da ${userInfo.created_user_name}`}</Typography>
                          </Stack> */}
                      </Card>
                    </Grid>

                    {/* Privacy Policy  */}
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          padding: '24px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'start',
                          gap: '16px',
                        }}
                      >
                        <CardHeader sx={{ padding: '0' }} title="Informazioni Utente" />

                        <Box sx={{ marginTop: '12px' }}>
                          <Typography>
                            <Typography variant="span" sx={{ fontWeight: 'bold' }}>
                              Privacy policy:{' '}
                            </Typography>
                            {userInfo.privacy_check
                              ? `Accettata il ${dayjs(userInfo.privacy_check_date).format('DD/MM/YYYY')}`
                              : `Non accettata`}
                          </Typography>

                          <Typography sx={{ marginTop: '12px' }}>
                            <Typography variant="span" sx={{ fontWeight: 'bold' }}>
                              Origine Utente:{' '}
                            </Typography>
                            {userInfo.user_origin}
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>

                    {/* Reset password card */}
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          padding: '24px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '16px',
                        }}
                      >
                        <CardHeader sx={{ padding: '0' }} title="Gestione Password" />
                        <Box sx={{ marginTop: '12px' }}>
                          <DefaultContainedButton disabled={!canModifyUser} onClick={handleOpenResetPassword}>
                            Reset Password
                          </DefaultContainedButton>

                          <Dialog fullWidth open={openResetPassword} onClose={() => {}}>
                            <DialogTitle sx={{ textAlign: 'center' }}>Reset Password</DialogTitle>

                            <form onSubmit={handleResetPassword}>
                              <DialogContent>
                                <Grid container spacing={3}>
                                  <Grid item xs={12} sx={{ overflow: 'auto' }}>
                                    <Box>
                                      Cliccando su 'Continua' verrà inviata tramite mail una password temporanea
                                      all'indirizzo:{' '}
                                      <Typography sx={{ display: 'inline', fontWeight: 'bold' }}>
                                        {userInfo.email}
                                      </Typography>{' '}
                                      . Seguendo le indicazioni della mail sarà possibile reimpostare la password.
                                    </Box>
                                  </Grid>
                                </Grid>

                                {isDialogOpen === 'resetPassword' && displayPopup()}
                              </DialogContent>
                              <DialogActions sx={{ paddingRight: 3, paddingBottom: 3 }}>
                                <DefaultOutlinedButton onClick={handleCloseResetPassword}>Chiudi</DefaultOutlinedButton>
                                <DefaultContainedButton type="submit" customStyles={{ height: '34px' }}>
                                  Continua
                                </DefaultContainedButton>
                              </DialogActions>
                            </form>
                          </Dialog>
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>

                {/* User data card */}
                <Grid item xs={12} md={8}>
                  <Card sx={{ p: '24px' }}>
                    <form onSubmit={updateUserData}>
                      {!isEditApisRunning ? (
                        <Grid container spacing={3} sx={{ justifyContent: 'start !important' }}>
                          {/* Peronal Title */}
                          <Grid item xs={12}>
                            <Typography variant="h6">Informazioni Personali</Typography>
                          </Grid>

                          {/* Name */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField
                              error={errors.name}
                              disabled={!edit || !canManageUser}
                              onChange={(e) => {
                                setName(e.currentTarget.value);
                              }}
                              value={name}
                              label="Nome"
                            />
                          </Grid>

                          {/* Surname */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField
                              disabled={!edit || !canManageUser}
                              error={errors.surname}
                              onChange={(e) => {
                                setSurname(e.currentTarget.value);
                              }}
                              value={surname}
                              label="Cognome"
                            />
                          </Grid>

                          {/* Alias */}
                          <Grid item xs={12} md={useAlias ? 6 : 12} sx={{ height: '80px' }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  disabled={!edit}
                                  checked={useAlias}
                                  onChange={(e) => {
                                    setUseAlias(e.target.checked);
                                  }}
                                />
                              }
                              label="Utilizza nome alias"
                            />
                          </Grid>
                          {useAlias && (
                            <>
                              {/* name alias */}
                              <Grid item xs={12} md={6}>
                                <CustomTextField
                                  error={errors.name_alias}
                                  disabled={!edit}
                                  onChange={(e) => {
                                    setAliasName(e.currentTarget.value);
                                  }}
                                  value={aliasName}
                                  label="Alias"
                                />
                              </Grid>
                            </>
                          )}

                          {/* gender */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              options={GenderList}
                              disabled={!edit}
                              getOptionLabel={(option) => option.gender}
                              isOptionEqualToValue={(option, value) => option.gender === value.gender}
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.gender}>
                                  {option.gender}
                                </Box>
                              )}
                              onChange={(e, value) => {
                                setGender(value);
                              }}
                              defaultValue={gender}
                              renderInput={(params) => (
                                <TextField {...params} label="Sesso" error={errors.gender} variant="outlined" />
                              )}
                            />
                          </Grid>

                          {/* Birth Date */}
                          <Grid item xs={12} md={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                              <CustomDatePicker
                                error={errors.birth_date}
                                disabled={!canManageUser || !edit}
                                label="Data di Nascita *"
                                value={dayjs(birthDate)}
                                onChange={handleBirthDateChange}
                              />
                            </LocalizationProvider>
                          </Grid>

                          {/* Birth Place */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              options={birthPlacesList}
                              disabled={!canManageUser || !edit}
                              getOptionLabel={(option) => option.name}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.id}>
                                  {option.name}
                                </Box>
                              )}
                              defaultValue={birthPlace}
                              onChange={(e, value) => {
                                setBirthPlace(value);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Luogo di Nascita"
                                  error={errors.place_of_birth}
                                  variant="outlined"
                                />
                              )}
                            />
                          </Grid>

                          {/* Fiscal Code */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField
                              error={errors.fiscal_code}
                              disabled={!canManageUser || !edit}
                              defaultValue={fiscalCode}
                              onChange={(e) => {
                                setFiscalCode(e.currentTarget.value);
                              }}
                              label="Codice Fiscale"
                            />
                          </Grid>

                          {/* Entity */}
                          <Grid item xs={12} md={6} sx={{ alignSelf: 'start' }}>
                            <CustomTextField
                              error={errors.entity_id}
                              disabled={!edit}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment
                                    position="end"
                                    sx={{ cursor: 'pointer', display: canModifyUser ? 'flex' : 'none' }}
                                  >
                                    <Tooltip title="Modifica" placement="top" arrow>
                                      <EditIcon
                                        onClick={() => {
                                          if (edit) {
                                            setOpenEntitiesFilter(!openEntitiesFilter);
                                          }
                                        }}
                                      />
                                    </Tooltip>
                                  </InputAdornment>
                                ),
                              }}
                              value={userEntity ? userEntity.name : ''}
                              label="Circolo"
                            />

                            <EntitiesDialog
                              openDialog={openEntitiesFilter}
                              closeDialog={handleCloseEntitiesFilterDialog}
                              outputEntity={getSelectedEntity}
                              context={'userDetails'}
                            />
                          </Grid>

                          {/* Email */}
                          <Grid
                            item
                            xs={12}
                            md={6}
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                          >
                            <CustomTextField
                              error={errors.email}
                              disabled={!canManageUser || !edit}
                              value={email}
                              onChange={(e) => {
                                setEmail(e.currentTarget.value);
                              }}
                              label="Email"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {userInfo.verified_email ? (
                                      <Tooltip arrow title="Email verificata" placement="top">
                                        <VerifiedIcon sx={{ color: 'green !important' }} />
                                      </Tooltip>
                                    ) : (
                                      <Tooltip arrow title="Email non verificata" placement="top">
                                        <WarningAmberIcon sx={{ color: 'orange !important' }} />
                                      </Tooltip>
                                    )}
                                  </InputAdornment>
                                ),
                              }}
                            />

                            {!userInfo.verified_email && (
                              <CustomIconButton sx={{ width: '40px', height: '40px' }} onClick={handleClickEmailMenu}>
                                <MoreVertIcon />
                              </CustomIconButton>
                            )}

                            <Menu anchorEl={emailMenuAnchor} open={openEmailMenu} onClose={handleCloseEmailMenu}>
                              <MenuItem
                                onClick={() => {
                                  handleOpenVerifyEmailDialog();
                                }}
                              >
                                Verifica con OTP
                              </MenuItem>

                              {canManageUser && (
                                <MenuItem
                                  onClick={() => {
                                    verifyUserEmail();
                                  }}
                                >
                                  Verifica Forzata
                                </MenuItem>
                              )}
                            </Menu>
                            {/* Email verification dialog */}
                            <Dialog fullWidth open={openVerifyEmailDialog} onClose={() => {}}>
                              <DialogTitle>Verifica E-mail</DialogTitle>

                              <DialogContent>
                                <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                                  {/* Email Address */}
                                  <Stack
                                    direction={is600Screen ? 'row' : 'column'}
                                    justifyContent="center"
                                    alignItems="center"
                                    gap="6px"
                                  >
                                    <Typography variant="h6">E-mail:</Typography>
                                    <Typography variant="span">{email}</Typography>
                                    {isUserVerifyingEmail && !verifiedEmail && (
                                      <Tooltip title="In attesa di verifica" placement="right">
                                        <AutorenewIcon sx={{ color: '#414670' }} />
                                      </Tooltip>
                                    )}
                                    {verifiedEmail && (
                                      <Tooltip title="Email verificata" placement="right">
                                        <CheckIcon sx={{ color: 'green' }} />
                                      </Tooltip>
                                    )}
                                  </Stack>
                                  {!verifiedEmail && isUserVerifyingEmail && (
                                    <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                                      {/* Input & Submit Button */}
                                      <Stack direction="row" justifyContent="center" alignItems="center" gap="12px">
                                        <CustomTextField
                                          size="small"
                                          inputProps={{ maxLength: 6 }}
                                          onChange={(e) => validatePhoneNumber(e, setEmailVerificationCode)}
                                          type="tel"
                                          label="Inserisci il codice"
                                        />
                                        <DefaultOutlinedButton onClick={() => verifyUserEmail()}>
                                          Verifica
                                        </DefaultOutlinedButton>
                                      </Stack>

                                      {/* Resend Email verifcation code */}
                                      <Box sx={{ textAlign: 'center' }}>
                                        <Typography>Non hai ricevuto l'email?</Typography>
                                        <Typography
                                          sx={{ textDecoration: 'underline', color: 'gray', cursor: 'pointer' }}
                                          onClick={() => sendOtpEmail()}
                                        >
                                          Inviala di nuovo
                                        </Typography>
                                      </Box>
                                    </Stack>
                                  )}

                                  {!verifiedEmail && !isUserVerifyingEmail && (
                                    <DefaultOutlinedButton onClick={() => sendOtpEmail()}>
                                      Verifica Email
                                    </DefaultOutlinedButton>
                                  )}
                                </Stack>
                                {isDialogOpen === 'email' && displayPopup()}
                              </DialogContent>

                              <DialogActions>
                                <DefaultOutlinedButton onClick={handleCloseVerifyEmailDialog}>
                                  Chiudi
                                </DefaultOutlinedButton>
                              </DialogActions>
                            </Dialog>
                          </Grid>

                          {/* Prefix */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              disabled={!canManageUser || !edit}
                              options={resCountriesList}
                              value={
                                resCountriesList.length > 0
                                  ? resCountriesList.find((option) => option.call_code === callCode) || null
                                  : { call_code: callCode }
                              }
                              getOptionLabel={(option) =>
                                typeof option === 'string' ? option : option.call_code || ''
                              }
                              isOptionEqualToValue={(option, value) =>
                                typeof value === 'string'
                                  ? option.call_code === value
                                  : option.call_code === value.call_code
                              }
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.id}>
                                  {option.name} {option.call_code}
                                </Box>
                              )}
                              filterOptions={(options, state) => {
                                const newOptions = [];
                                options.forEach((element) => {
                                  if (
                                    element.name.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                                    element.call_code.toLowerCase().includes(state.inputValue.toLowerCase())
                                  ) {
                                    newOptions.push(element);
                                  }
                                });
                                return newOptions;
                              }}
                              onChange={(e, value) => {
                                setCallCode(value?.call_code || value || '');
                              }}
                              renderInput={(params) => (
                                <TextField {...params} label="Prefisso *" error={errors.call_code} variant="outlined" />
                              )}
                            />
                          </Grid>

                          {/* Phone */}
                          <Grid
                            item
                            xs={12}
                            md={6}
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                          >
                            <CustomTextField
                              disabled={!canManageUser || !edit}
                              error={errors.phone}
                              value={phoneNumber}
                              onChange={(event) => validatePhoneNumber(event, setPhoneNumber)}
                              type="tel"
                              label="Cellulare"
                              onKeyDown={(e) => {
                                if (e.ctrlKey || e.metaKey) {
                                  if (['c', 'v', 'x', 'a', 'z'].includes(e.key.toLowerCase())) {
                                    return;
                                  }
                                }
                              
                                if (
                                  e.key !== 'Backspace' &&
                                  !/[0-9]/.test(e.key) &&
                                  !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Delete'].includes(e.key)
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              onPaste={(e) => {
                                const paste = e.clipboardData.getData('text');
                                if (!/^\d+$/.test(paste)) {
                                  e.preventDefault();
                                }
                              }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {userInfo.verified_phone ? (
                                      <Tooltip arrow title="Numero verificato" placement="top">
                                        <VerifiedIcon sx={{ color: 'green !important' }} />
                                      </Tooltip>
                                    ) : (
                                      <Tooltip arrow title="Numero non verificato" placement="top">
                                        <WarningAmberIcon sx={{ color: 'orange !important' }} />
                                      </Tooltip>
                                    )}
                                  </InputAdornment>
                                ),
                              }}
                            />

                            {!userInfo.verified_phone && (
                              <CustomIconButton sx={{ width: '40px', height: '40px' }} onClick={handleClickPhoneMenu}>
                                <MoreVertIcon />
                              </CustomIconButton>
                            )}

                            <Menu anchorEl={phoneMenuAnchor} open={openPhoneMenu} onClose={handleClosePhoneMenu}>
                              <MenuItem
                                onClick={() => {
                                  handleOpenVerifyPhoneDialog();
                                }}
                              >
                                Verifica con OTP
                              </MenuItem>

                              {canManageUser && (
                                <MenuItem
                                  onClick={() => {
                                    verifyUserPhone();
                                  }}
                                >
                                  Verifica Forzata
                                </MenuItem>
                              )}
                            </Menu>

                            {/* Phone number verification dialog */}
                            <Dialog fullWidth open={openVerifyPhoneDialog} onClose={() => {}}>
                              <DialogTitle>Verifica Numero</DialogTitle>

                              <DialogContent>
                                {/* Phone Verification */}
                                <Stack
                                  justifyContent="center"
                                  alignItems="center"
                                  gap="12px"
                                  id="register-email-token-container"
                                >
                                  <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                                    {/* Phone Number */}
                                    <Stack
                                      direction={is600Screen ? 'row' : 'column'}
                                      justifyContent="center"
                                      alignItems="center"
                                      gap="6px"
                                    >
                                      <Typography variant="h6">Numero di telefono:</Typography>
                                      <Typography variant="span">
                                        {callCode}
                                        {phoneNumber}
                                      </Typography>
                                      {isUserVerifyingPhone && !verifiedPhone && (
                                        <Tooltip title="In attesa di verifica" placement="right">
                                          <AutorenewIcon sx={{ color: '#414670' }} />
                                        </Tooltip>
                                      )}

                                      {verifiedPhone && (
                                        <Tooltip title="Numero verificato" placement="right">
                                          <CheckIcon sx={{ color: 'green' }} />
                                        </Tooltip>
                                      )}
                                    </Stack>

                                    {isUserVerifyingPhone && !verifiedPhone && (
                                      <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                                        {/* Input & Submit Button */}
                                        <Stack direction="row" justifyContent="center" alignItems="center" gap="12px">
                                          <CustomTextField
                                            size="small"
                                            inputProps={{ maxLength: 6 }}
                                            onChange={(e) => validatePhoneNumber(e, setPhoneVerificationCode)}
                                            label="Inserisci il codice"
                                          />
                                          <DefaultOutlinedButton
                                            disabled={verifiedPhone}
                                            onClick={() => verifyUserPhone()}
                                          >
                                            Verifica
                                          </DefaultOutlinedButton>
                                        </Stack>

                                        {/* Resend SMS */}
                                        <Box sx={{ textAlign: 'center' }}>
                                          <Typography>Non hai ricevuto l'sms?</Typography>
                                          <Typography
                                            sx={{ textDecoration: 'underline', color: 'gray', cursor: 'pointer' }}
                                            onClick={() => sendOtpSms()}
                                          >
                                            Invialo di nuovo
                                          </Typography>
                                        </Box>
                                      </Stack>
                                    )}
                                    {!verifiedPhone && !isUserVerifyingPhone && (
                                      <DefaultOutlinedButton onClick={() => sendOtpSms()}>
                                        Verifica Numero
                                      </DefaultOutlinedButton>
                                    )}
                                  </Stack>
                                </Stack>
                                {isDialogOpen === 'phone' && displayPopup()}
                              </DialogContent>
                              <DialogActions>
                                <DefaultOutlinedButton onClick={handleCloseVerifyPhoneDialog}>
                                  Chiudi
                                </DefaultOutlinedButton>
                              </DialogActions>
                            </Dialog>
                          </Grid>

                          {/* Role */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              multiple
                              disabled={!canManageUserRoles || !edit}
                              options={rolesList.filter((role) => role.prof_type_name !== 'BACKOFFICE')}
                              value={userRoles}
                              isOptionEqualToValue={(option, value) => option.prof_type_id === value.prof_type_id}
                              getOptionLabel={(option) => option.prof_type_name}
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.prof_type_id}>
                                  {option.prof_type_name}
                                </Box>
                              )}
                              onChange={(e, value) => {
                                setUserRoles(value);
                              }}
                              renderInput={(params) => (
                                <TextField {...params} label="Ruolo" error={errors.user_roles} variant="outlined" />
                              )}
                            />
                          </Grid>

                          {/* Res Title */}
                          <Grid item xs={12}>
                            <Typography variant="h6">Informazioni di Residenza</Typography>
                          </Grid>

                          {/* Res Country */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              required
                              id="res-country"
                              disabled={!edit}
                              options={
                                resCountry?.name === italy.name
                                  ? resCountriesList.filter(
                                      (country) =>
                                        (!resRegion || country.id === resRegion.country_id) &&
                                        (!resProvince || country.id === resProvince.country_id)
                                    )
                                  : resCountriesList
                              }
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              getOptionLabel={(option) => option.name}
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.id}>
                                  {option.name}
                                </Box>
                              )}
                              value={resCountry}
                              onChange={(e, value) => {
                                setResCountry(value);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Paese *"
                                  error={errors.res_country_id}
                                  variant="outlined"
                                />
                              )}
                            />
                          </Grid>

                          {/* Res Region */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              id="res-region"
                              required
                              freeSolo={resCountry?.name !== italy?.name}
                              disabled={!edit}
                              options={resRegionList.filter(
                                (region) =>
                                  (!resCountry || region.country_id === resCountry.id) &&
                                  (!resProvince || region.region_name === resProvince.region_name)
                              )}
                              isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                              getOptionLabel={(option) => {
                                if (typeof option === 'string') {
                                  return option;
                                }
                                if (option.inputValue) {
                                  return option.inputValue;
                                }
                                return option.region_name;
                              }}
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.region_name}>
                                  {option.region_name}
                                </Box>
                              )}
                              value={
                                resRegionName === undefined ||
                                resRegionName === null ||
                                resCountry?.name === italy?.name
                                  ? resRegion
                                  : resRegionName
                              }
                              onChange={(e, value) => {
                                if (typeof value === 'string') {
                                  setResRegion(value);
                                } else if (value && value.inputValue) {
                                  setResRegion(value.inputValue);
                                } else {
                                  setResRegion(value);
                                }
                              }}
                              onInputChange={(e, value) => {
                                if(resCountry?.name !== italy?.name){
                                  setResRegionName(value);
                                }else{
                                  setResRegionName(null)
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Regione *"
                                  error={errors.res_region_name}
                                  variant="outlined"
                                />
                              )}
                            />
                          </Grid>

                          {/* Res Province */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              id="res-province"
                              required
                              freeSolo={resCountry?.name !== italy?.name}
                              disabled={!edit || (resCountry && resCountry?.name !== italy.name)}
                              options={resProvincesList.filter(
                                (province) =>
                                  (!resRegion || province.region_name === resRegion.region_name) &&
                                  (!resCity || province.id === resCity.province_id) &&
                                  (!resCountry || province.country_id === resCountry.id)
                              )}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              getOptionLabel={(option) => {
                                if (typeof option === 'string') {
                                  return option;
                                }
                                if (option.inputValue) {
                                  return option.inputValue;
                                }
                                return option.name;
                              }}
                              value={resProvince}
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.id}>
                                  {option.name}
                                </Box>
                              )}
                              onChange={(e, value) => {
                                if (typeof value === 'string') {
                                  setResProvince(value);
                                } else if (value && value.inputValue) {
                                  setResProvince(value.inputValue);
                                } else {
                                  setResProvince(value);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={`Provincia ${resCountry?.name === italy.name ? '*' : ''}`}
                                  variant="outlined"
                                  error={errors.res_province_id}
                                />
                              )}
                            />
                          </Grid>

                          {/* Res City */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              id="res-city"
                              required
                              freeSolo={resCountry?.name !== italy?.name}
                              disabled={!edit}
                              options={resCitiesList.filter(
                                (city) => !resProvince || city.province_id === resProvince.id
                              )}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              getOptionLabel={(option) => {
                                if (typeof option === 'string') {
                                  return option;
                                }
                                if (option.inputValue) {
                                  return option.inputValue;
                                }
                                return option.name;
                              }}
                              value={
                                resCityName === undefined || resCityName === null || resCountry?.name === italy?.name
                                  ? resCity
                                  : resCityName
                              }
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.id}>
                                  {option.name}
                                </Box>
                              )}
                              onChange={(e, value) => {
                                if (typeof value === 'string') {
                                  setResCity(value);
                                } else if (value && value.inputValue) {
                                  setResCity(value.inputValue);
                                } else {
                                  setResCity(value);
                                }
                              }}
                              onInputChange={(e, value) => {
                                if(resCountry?.name !== italy?.name){
                                  setResCityName(value);
                                }else{
                                  setResCityName(null)
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Comune *"
                                  variant="outlined"
                                  error={errors.res_city_id || errors.res_city_name}
                                />
                              )}
                            />
                          </Grid>

                          {/* Res Street */}
                          <Grid item xs={12} sm={6}>
                            <CustomTextField
                              id="res-street"
                              error={errors.res_street}
                              disabled={!edit}
                              fullWidth
                              name="resStreet"
                              label="Indirizzo di Residenza"
                              value={resStreet}
                              onChange={(e) => {
                                setResStreet(e.target.value);
                              }}
                            />
                          </Grid>

                          {/* Res Zip Code */}
                          <Grid item xs={12} sm={6}>
                            <CustomTextField
                              id="res-zipcode"
                              error={errors.res_zip_code}
                              disabled={!edit}
                              fullWidth
                              label="CAP di Residenza"
                              name="resZipCode"
                              onChange={(e) => {
                                setResZipCode(e.target.value);
                              }}
                              value={resZipCode}
                            />
                          </Grid>

                          {/* Domicile */}
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Switch
                                  color="primary"
                                  checked={domIsRes}
                                  onChange={handleSwitchChange}
                                  disabled={!edit}
                                />
                              }
                              label="Le informazioni di Domicilio corrispondono a quelle di Residenza"
                            />
                          </Grid>

                          {/* Domicile info container */}
                          {!domIsRes && (
                            <Grid container spacing={3} sx={{ margin: '0' }}>
                              {/* Dom Title */}
                              <Grid item xs={12}>
                                <Typography variant="h6">Informazioni di Domicilio</Typography>
                              </Grid>

                              {/* Dom Country */}
                              <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                  id="dom-country"
                                  disabled={!edit}
                                  options={
                                    domCountry?.name === italy.name
                                      ? domCountriesList.filter(
                                          (country) =>
                                            (!domRegion || country.id === domRegion.country_id) &&
                                            (!domProvince || country.id === domProvince.country_id)
                                        )
                                      : domCountriesList
                                  }
                                  getOptionLabel={(option) => option.name}
                                  isOptionEqualToValue={(option, value) => option.id === value.id}
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id}>
                                      {option.name}
                                    </Box>
                                  )}
                                  value={domCountry}
                                  onChange={(e, value) => {
                                    setDomCountry(value);
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Paese *"
                                      error={errors.home_country_id}
                                      variant="outlined"
                                    />
                                  )}
                                />
                              </Grid>

                              {/* Dom Region */}
                              <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                  id="dom-region"
                                  freeSolo={domCountry?.name !== italy?.name}
                                  disabled={!edit}
                                  options={domRegionList.filter(
                                    (region) =>
                                      (!domCountry || region.country_id === domCountry.id) &&
                                      (!domProvince || region.region_name === domProvince.region_name)
                                  )}
                                  isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                                  getOptionLabel={(option) => {
                                    if (typeof option === 'string') {
                                      return option;
                                    }
                                    if (option.inputValue) {
                                      return option.inputValue;
                                    }
                                    return option.region_name || '';
                                  }}
                                  value={
                                    domRegionName !== null &&
                                    domRegionName !== undefined &&
                                    domCountry?.name !== italy?.name
                                      ? domRegionName
                                      : domRegion
                                  }
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.region_name}>
                                      {option.region_name}
                                    </Box>
                                  )}
                                  onChange={(e, value) => {
                                    if (typeof value === 'string') {
                                      setDomRegion(value);
                                    } else if (value && value.inputValue) {
                                      setDomRegion(value.inputValue);
                                    } else {
                                      setDomRegion(value);
                                    }
                                  }}
                                  onInputChange={(e, value) => {
                                    if(domCountry?.name !== italy?.name){
                                      setDomRegionName(value);
                                    }else{
                                      setDomRegionName(null)
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      error={errors.home_region_name}
                                      label="Regione *"
                                      variant="outlined"
                                    />
                                  )}
                                />
                              </Grid>

                              {/* Dom Province */}
                              <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                  id="dom-province"
                                  freeSolo={domCountry?.name !== italy?.name}
                                  disabled={!edit || (domCountry && domCountry?.name !== italy.name)}
                                  options={domProvincesList.filter(
                                    (province) =>
                                      (!domRegion || province.region_name === domRegion.region_name) &&
                                      (!domCity || province.id === domCity.province_id)
                                  )}
                                  isOptionEqualToValue={(option, value) => value.id === option.id}
                                  getOptionLabel={(option) => {
                                    if (typeof option === 'string') {
                                      return option;
                                    }
                                    if (option.inputValue) {
                                      return option.inputValue;
                                    }
                                    return option.name || '';
                                  }}
                                  value={domProvince}
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id}>
                                      {option.name}
                                    </Box>
                                  )}
                                  onChange={(e, value) => {
                                    if (typeof value === 'string') {
                                      setDomProvince(value);
                                    } else if (value && value.inputValue) {
                                      setDomProvince(value.inputValue);
                                    } else {
                                      setDomProvince(value);
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={`Provincia ${domCountry?.name === italy.name ? '*' : ''}`}
                                      variant="outlined"
                                      error={errors.home_province_id}
                                    />
                                  )}
                                />
                              </Grid>

                              {/* Dom City */}
                              <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                  id="dom-city"
                                  freeSolo={domCountry?.name !== italy?.name}
                                  disabled={!edit}
                                  options={domCitiesList.filter(
                                    (city) => !domProvince || city.province_id === domProvince.id
                                  )}
                                  isOptionEqualToValue={(option, value) => value.id === option.id}
                                  getOptionLabel={(option) => {
                                    if (typeof option === 'string') {
                                      return option;
                                    }
                                    if (option.inputValue) {
                                      return option.inputValue;
                                    }
                                    return option.name || '';
                                  }}
                                  value={
                                    domCityName !== null &&
                                    domCityName !== undefined &&
                                    domCountry?.name !== italy?.name
                                      ? domCityName
                                      : domCity
                                  }
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id}>
                                      {option.name}
                                    </Box>
                                  )}
                                  onChange={(e, value) => {
                                    if (typeof value === 'string') {
                                      setDomCity(value);
                                    } else if (value && value.inputValue) {
                                      setDomCity(value.inputValue);
                                    } else {
                                      setDomCity(value);
                                    }
                                  }}
                                  onInputChange={(e, value) => {
                                    if(domCountry?.name !== italy?.name){
                                      setDomCityName(value);
                                    }else{
                                      setDomCityName(null)
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Comune *"
                                      variant="outlined"
                                      error={errors.home_city_id || errors.home_city_name}
                                    />
                                  )}
                                />
                              </Grid>

                              {/* Dom Street */}
                              <Grid item xs={12} sm={6}>
                                <CustomTextField
                                  id="dom-street"
                                  disabled={!edit}
                                  fullWidth
                                  label="Indirizzo di Domicilio *"
                                  onChange={(e) => setDomStreet(e.target.value)}
                                  value={domStreet}
                                  error={errors.home_street}
                                />
                              </Grid>

                              {/* Dom Zip Code */}
                              <Grid item xs={12} sm={6}>
                                <CustomTextField
                                  id="dom-zipcode"
                                  disabled={!edit}
                                  fullWidth
                                  label="CAP di Domicilio *"
                                  onChange={(e) => {
                                    setDomZipCode(e.currentTarget.value);
                                  }}
                                  value={domZipCode}
                                  error={errors.home_zip_code}
                                />
                              </Grid>
                            </Grid>
                          )}

                          {/* Submit / close buttons */}
                          <Grid item xs={12} sx={{ display: canModifyUser ? 'block' : 'none' }}>
                            <Stack direction="row" justifyContent="end" gap="12px">
                              {edit && (
                                <DefaultOutlinedButton onClick={handleCloseEditButtonClick}>
                                  Annulla
                                </DefaultOutlinedButton>
                              )}
                              <DefaultContainedButton type="submit">
                                {edit ? 'Salva' : 'Modifica'}
                              </DefaultContainedButton>

                              <CustomSnackbar
                                open={openEditUserDataSnack}
                                handleClose={handleCloseEditUserDataSnack}
                                message="Inserisci i dati mancanti!"
                              />
                            </Stack>
                          </Grid>
                        </Grid>
                      ) : (
                        // Loading
                        <Stack
                          direction="row"
                          justifyContent="center"
                          alignItems="center"
                          sx={{
                            minHeight: domIsRes ? '764px' : '1052px',
                          }}
                        >
                          <CircularProgress />
                        </Stack>
                      )}
                    </form>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              // Error handler
              ErrorUI(errorMessage)
            )}
          </QuintupleTabPanel>

          {/* Cards tab */}
          <QuintupleTabPanel value={value} index={1}>
            {/* Loading */}
            {isLoading ? (
              LoadingUI()
            ) : errorMessage.length === 0 ? (
              <>
                <Stack
                  direction={isSmallScreen ? 'row' : 'column'}
                  justifyContent="end"
                  alignItems="center"
                  // sx={{ padding: '0 0 24px' }}
                >
                  {/* Card action buttons */}
                  <Stack direction="row" gap="10px">
                    <DefaultContainedButton
                      disabled={!canModifyUser || isBlacklistedEntity}
                      onClick={handleOpenNewExpiredCard}
                      startIcon={<AddIcon />}
                    >
                      Crea Tessera Precedente
                    </DefaultContainedButton>

                    <DefaultContainedButton
                      disabled={disableNewCard || !canModifyUser || isBlacklistedEntity}
                      onClick={handleOpenNewCard}
                      startIcon={<AddIcon />}
                    >
                      Crea Tessera
                    </DefaultContainedButton>
                  </Stack>

                  {/* New card dialog */}
                  <Dialog fullWidth open={openNewCard} onClose={() => {}}>
                    {activeRatesApiCalled && subscriptionApiError.length === 0 ? (
                      <form onSubmit={onCreateMembershipCard}>
                        <DialogContent>
                          {activeCampaign?.description && (
                            <DialogTitle>{`Tesseramento ${activeCampaign.description}`}</DialogTitle>
                          )}
                          <Alert severity="error" variant="filled" sx={{ marginBottom: '8px' }}>
                            Se ricopri incarichi istituzionali (consigliere/a, assessore/a, sindaco/a, presidente,
                            parlamentare) inserisci la tua carica nella relativa sezione (“cariche”) prima di procedere
                            al tesseramento.
                          </Alert>
                          <Grid container spacing={3}>
                            <Grid item xs={12} sx={{ overflow: 'auto' }}>
                              {userRates.length > 0 ? (
                                <>
                                  <TableContainer>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell align="center">Tariffa</TableCell>
                                          <TableCell align="right">Costo</TableCell>
                                          <TableCell align="center">Valuta</TableCell>
                                          <TableCell align="center">Modificabile</TableCell>
                                        </TableRow>
                                      </TableHead>

                                      <TableBody>
                                        {userRates.map((rate, index) => (
                                          <TableRow hover key={rate.id}>
                                            <TableCell align="center">{rate.name}</TableCell>
                                            {/* <TableCell sx={{ minWidth: '60px' }} align="right">
                                              {disabledStates[index] ? (
                                                // Se l'elemento è in modalità modifica
                                                <CustomTextField
                                                  select
                                                  onChange={(e) => handleRateChange(index, e.target.value)}
                                                  label="Importo"
                                                  defaultValue={0}
                                                >
                                                  <MenuItem value={0}>0</MenuItem>
                                                  <MenuItem value={5}>5</MenuItem>
                                                  <MenuItem value={10}>10</MenuItem>
                                                  <MenuItem value={20}>20</MenuItem>
                                                  <MenuItem value={50}>50</MenuItem>
                                                  <MenuItem value={100}>100</MenuItem>
                                                </CustomTextField>
                                              ) : (
                                                // Se l'elemento è in modalità visualizzazione
                                                <Typography>
                                                  {isGdAvailable ? rate.price_discounted : rate.price}
                                                </Typography>
                                              )}
                                            </TableCell> */}
                                            <TableCell sx={{ minWidth: '40px' }} align="right">
                                              {disabledStates[index] ? (
                                                otherValue[index]?.isOther ? (
                                                  <CustomTextField
                                                    value={otherValue[index]?.value || ''}
                                                    onKeyDown={(e) => {
                                                      const allowedKeys = ['Backspace', 'Tab', '.', ','];
                                                      if (!allowedKeys.includes(e.key) && !/^[0-9]$/.test(e.key)) {
                                                        e.preventDefault();
                                                      }
                                                    }}
                                                    onChange={(e) => {
                                                      const inputValue = e.target.value.replace(',', '.');
                                                      setOtherValue({
                                                        ...otherValue,
                                                        [index]: {
                                                          ...otherValue[index],
                                                          value: inputValue,
                                                        },
                                                      });
                                                      handleRateChange(index, inputValue);
                                                    }}
                                                    label="Importo personalizzato"
                                                  />
                                                ) : (
                                                  <CustomTextField
                                                    customStyles={{ width: '100px' }}
                                                    select
                                                    onChange={(e) => {
                                                      const selectedValue = e.target.value;
                                                      if (selectedValue === -1) {
                                                        setOtherValue({
                                                          ...otherValue,
                                                          [index]: { isOther: true, value: '' },
                                                        });
                                                      } else {
                                                        setOtherValue({
                                                          ...otherValue,
                                                          [index]: { isOther: false, value: selectedValue },
                                                        });
                                                        handleRateChange(index, selectedValue);
                                                      }
                                                    }}
                                                    label="Importo"
                                                    value={otherValue[index]?.value || ''}
                                                  >
                                                    <MenuItem value={0}>0</MenuItem>
                                                    <MenuItem value={5}>5</MenuItem>
                                                    <MenuItem value={10}>10</MenuItem>
                                                    <MenuItem value={20}>20</MenuItem>
                                                    <MenuItem value={50}>50</MenuItem>
                                                    <MenuItem value={100}>100</MenuItem>
                                                    <MenuItem value={200}>200</MenuItem>
                                                    <MenuItem value={500}>500</MenuItem>
                                                    <MenuItem value={-1}>Altro</MenuItem>
                                                  </CustomTextField>
                                                )
                                              ) : (
                                                <Typography>
                                                  {isGdAvailable ? rate.price_discounted : rate.price}
                                                </Typography>
                                              )}
                                            </TableCell>
                                            <TableCell align="center">{rate.currency}</TableCell>
                                            <TableCell align="center">
                                              {rate.has_editable === 1 ? (
                                                <DefaultOutlinedButton
                                                  onClick={() => handleDisabledInput(index)}
                                                  className="rate-modify-button"
                                                >
                                                  {disabledStates[index] ? 'Salva' : 'Modifica'}
                                                </DefaultOutlinedButton>
                                              ) : (
                                                <DefaultOutlinedButton
                                                  onClick={() => handleDisabledInput(index)}
                                                  disabled
                                                  className="rate-modify-button"
                                                >
                                                  Fissa
                                                </DefaultOutlinedButton>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 'bold' }} align="center">
                                            Totale
                                          </TableCell>
                                          <TableCell align="right" sx={{ fontSize: '1em', fontWeight: 'bold' }}>
                                            {!Number.isNaN(totalPrice) ? totalPrice.toFixed(2) : 'Valore non valido'}
                                          </TableCell>
                                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                            EUR
                                          </TableCell>
                                          <TableCell />
                                        </TableRow>
                                      </TableBody>
                                    </Table>
                                  </TableContainer>

                                  <FormControlLabel
                                    sx={{ paddingTop: '16px' }}
                                    control={
                                      <Switch
                                        checked={onlineMembershipCard}
                                        onChange={() => setOnlineMembershipCard(!onlineMembershipCard)}
                                      />
                                    }
                                    label={onlineMembershipCard ? 'Tessera Online' : 'Tessera Cartacea'}
                                  />
                                </>
                              ) : (
                                <Typography color="error">La campagna selezionata non ha tariffe</Typography>
                              )}
                            </Grid>
                          </Grid>
                        </DialogContent>

                        <DialogActions sx={{ paddingBottom: 3, paddingRight: 3 }}>
                          <DefaultOutlinedButton onClick={handleCloseNewCard}>Chiudi</DefaultOutlinedButton>
                          <DefaultContainedButton type="submit" disabled={!isSaveEnabled}>
                            Crea
                          </DefaultContainedButton>
                        </DialogActions>
                      </form>
                    ) : subscriptionApiError?.length > 0 ? (
                      ErrorUI(subscriptionApiError)
                    ) : (
                      LoadingUI()
                    )}
                    {!activeRatesApiCalled && (
                      <DialogActions sx={{ paddingBottom: 3, paddingRight: 3 }}>
                        <DefaultOutlinedButton onClick={handleCloseNewCard}>Chiudi</DefaultOutlinedButton>
                      </DialogActions>
                    )}
                    {isDialogOpen === 'createCard' && displayPopup()}
                  </Dialog>

                  {/* New expired card dialog */}
                  <Dialog fullWidth open={openNewExpiredCard} onClose={() => {}}>
                    {expiredCampaigns.length > 0 && expiredSubscriptionApiError.length === 0 ? (
                      <>
                        <DialogContent>
                          <DialogTitle>Creazione Tesseramento Precedente</DialogTitle>

                          <Grid container spacing={3}>
                            <Grid item xs={12} sx={{ overflow: 'auto' }}>
                              {displayExpiredOptions ? (
                                <Box sx={{ width: '100%' }}>
                                  <CustomTextField
                                    customStyles={{ width: '80%', minWidth: '250px' }}
                                    select
                                    onChange={(e) => {
                                      setSelectedExpiredCampaign(e.target.value);
                                    }}
                                    label="Seleziona Campagna"
                                    value={selectedExpiredCampaign}
                                  >
                                    {expiredCampaigns.map((expiredCampaign) => (
                                      <MenuItem key={expiredCampaign.id} value={expiredCampaign}>
                                        {expiredCampaign.description}
                                      </MenuItem>
                                    ))}
                                  </CustomTextField>
                                </Box>
                              ) : expiredRatesApiCalled ? (
                                <>
                                  <TableContainer>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell align="center">Tariffa</TableCell>
                                          <TableCell align="right">Costo</TableCell>
                                          <TableCell align="center">Valuta</TableCell>
                                          <TableCell />
                                        </TableRow>
                                      </TableHead>

                                      <TableBody>
                                        {expiredRates.map((rate, index) => (
                                          <TableRow key={rate.id}>
                                            <TableCell align="center">{rate.name}</TableCell>
                                            {/* <TableCell align="right">
                                              {isGdAvailable ? rate.price_discounted : rate.price}
                                            </TableCell> */}
                                            <TableCell sx={{ minWidth: '40px' }} align="right">
                                              {disabledExpiredStates[index] ? (
                                                otherExpiredValue[index]?.isOther ? (
                                                  <CustomTextField
                                                    value={otherExpiredValue[index]?.value || ''}
                                                    onKeyDown={(e) => {
                                                      const allowedKeys = ['Backspace', 'Tab', '.', ','];
                                                      if (!allowedKeys.includes(e.key) && !/^[0-9]$/.test(e.key)) {
                                                        e.preventDefault();
                                                      }
                                                    }}
                                                    onChange={(e) => {
                                                      const inputValue = e.target.value.replace(',', '.');
                                                      setOtherExpiredValue({
                                                        ...otherExpiredValue,
                                                        [index]: {
                                                          ...otherExpiredValue[index],
                                                          value: inputValue,
                                                        },
                                                      });
                                                      handleExpiredRateChange(index, inputValue);
                                                    }}
                                                    label="Importo personalizzato"
                                                  />
                                                ) : (
                                                  <CustomTextField
                                                    customStyles={{ width: '100px' }}
                                                    select
                                                    onChange={(e) => {
                                                      const selectedValue = e.target.value;
                                                      if (selectedValue === -1) {
                                                        setOtherExpiredValue({
                                                          ...otherExpiredValue,
                                                          [index]: { isOther: true, value: '' },
                                                        });
                                                      } else {
                                                        setOtherExpiredValue({
                                                          ...otherExpiredValue,
                                                          [index]: { isOther: false, value: selectedValue },
                                                        });
                                                        handleExpiredRateChange(index, selectedValue);
                                                      }
                                                    }}
                                                    label="Importo"
                                                    value={otherExpiredValue[index]?.value || ''}
                                                  >
                                                    <MenuItem value={0}>0</MenuItem>
                                                    <MenuItem value={5}>5</MenuItem>
                                                    <MenuItem value={10}>10</MenuItem>
                                                    <MenuItem value={20}>20</MenuItem>
                                                    <MenuItem value={50}>50</MenuItem>
                                                    <MenuItem value={100}>100</MenuItem>
                                                    <MenuItem value={200}>200</MenuItem>
                                                    <MenuItem value={500}>500</MenuItem>
                                                    <MenuItem value={-1}>Altro</MenuItem>
                                                  </CustomTextField>
                                                )
                                              ) : (
                                                <Typography>
                                                  {isGdAvailable ? rate.price_discounted : rate.price}
                                                </Typography>
                                              )}
                                            </TableCell>
                                            <TableCell align="center">{rate.currency}</TableCell>
                                            <TableCell align="center">
                                              {rate.has_editable === 1 ? (
                                                <DefaultOutlinedButton
                                                  onClick={() => handleDisabledExpiredInput(index)}
                                                  className="rate-modify-button"
                                                >
                                                  {disabledExpiredStates[index] ? 'Salva' : 'Modifica'}
                                                </DefaultOutlinedButton>
                                              ) : (
                                                <DefaultOutlinedButton
                                                  onClick={() => handleDisabledExpiredInput(index)}
                                                  disabled
                                                  className="rate-modify-button"
                                                >
                                                  Fissa
                                                </DefaultOutlinedButton>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        ))}

                                        {/* Rates */}
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 'bold' }} align="center">
                                            Totale
                                          </TableCell>
                                          <TableCell align="right" sx={{ fontSize: '1em', fontWeight: 'bold' }}>
                                            {!Number.isNaN(expiredRatesTotalPrice)
                                              ? expiredRatesTotalPrice.toFixed(2)
                                              : 'Valore non valido'}
                                          </TableCell>
                                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                            EUR
                                          </TableCell>
                                          <TableCell />
                                        </TableRow>
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </>
                              ) : (
                                LoadingUI()
                              )}
                            </Grid>
                          </Grid>
                        </DialogContent>

                        <DialogActions sx={{ paddingBottom: 3, paddingRight: 3 }}>
                          {displayExpiredOptions ? (
                            <>
                              <DefaultOutlinedButton onClick={handleCloseNewExpiredCard}>Chiudi</DefaultOutlinedButton>
                              <DefaultContainedButton onClick={handleSelectedExpiredCampaign}>
                                Seleziona
                              </DefaultContainedButton>
                            </>
                          ) : (
                            <>
                              <DefaultOutlinedButton onClick={handleCloseNewExpiredCard}>Chiudi</DefaultOutlinedButton>
                              <DefaultContainedButton onClick={handleCreateExpiredCard}>Crea</DefaultContainedButton>
                            </>
                          )}
                          {isDialogOpen === 'createExpiredCard' && displayPopup()}
                        </DialogActions>
                      </>
                    ) : expiredSubscriptionApiError?.length > 0 ? (
                      ErrorUI(expiredSubscriptionApiError)
                    ) : (
                      LoadingUI()
                    )}
                    {expiredCampaigns.length === 0 && (
                      <DialogActions sx={{ paddingBottom: 3, paddingRight: 3 }}>
                        <DefaultOutlinedButton onClick={handleCloseNewExpiredCard}>Chiudi</DefaultOutlinedButton>
                      </DialogActions>
                    )}
                    {isDialogOpen === 'createCard' && displayPopup()}
                  </Dialog>
                </Stack>

                {isBlacklistedEntity ? (
                  <Alert variant="filled" severity="error" sx={{ marginTop: '24px' }}>
                    Al momento il tesseramento è stato disabilitato.
                  </Alert>
                ) : (
                  <Alert variant="filled" severity="error" sx={{ marginTop: '24px' }}>
                    Se ricopri incarichi istituzionali (consigliere/a, assessore/a, sindaco/a, presidente, parlamentare)
                    inserisci la tua carica nella relativa sezione (“cariche”) prima di procedere al tesseramento.{' '}
                  </Alert>
                )}

                <Card sx={{ padding: '24px', marginTop: '10px' }}>
                  {/* Table */}
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Campagna</TableCell>
                          <TableCell>Stato Tessera</TableCell>
                          <TableCell>Entità</TableCell>
                          <TableCell>Data Tesseramento</TableCell>
                          <TableCell>Numero Tessera</TableCell>
                          <TableCell>Metodo di Pagamento</TableCell>
                          <TableCell>Quota Tessera</TableCell>
                          <TableCell>Stato Pagamento</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      {cards.length > 0 ? (
                        <TableBody>
                          {/* Edit Card */}
                          <Dialog open={openEditCard} fullWidth onClose={() => {}}>
                            <DialogTitle sx={{ textAlign: 'center' }}>Pagamento Tessera</DialogTitle>
                            {statusApiCalled && typesApiCalled ? (
                              <>
                                <form onSubmit={handleEditCard}>
                                  <DialogContent>
                                    <Grid container spacing={3}>
                                      {/* Campaign Name */}
                                      <Grid item xs={12}>
                                        <CustomTextField
                                          disabled
                                          name="name"
                                          label="Nome Campagna"
                                          defaultValue={cardToEdit.campaign_name}
                                        />
                                      </Grid>

                                      {/* Entity name */}
                                      <Grid item xs={12}>
                                        <CustomTextField
                                          disabled
                                          name="entity"
                                          label="Entità"
                                          defaultValue={cardToEdit.payment_entity}
                                        />
                                      </Grid>

                                      <Grid item xs={12}>
                                        <CustomTextField
                                          disabled
                                          name="date"
                                          label="Data Tesseramento"
                                          defaultValue={dayjs(cardToEdit.creation_date).format('DD/MM/YYYY')}
                                        />
                                      </Grid>

                                      {/* Card payment type */}
                                      <Grid item xs={12}>
                                        <FormControl color="secondary" sx={{ width: '100%' }}>
                                          <InputLabel>Tipo Pagamento</InputLabel>
                                          <Select
                                            sx={{ width: '100%' }}
                                            value={cardToEditPaymentTypeType}
                                            label="Tipo Pagamento"
                                            disabled={cardToEditPaymentStatusType === 'P' && !canManageUser}
                                            onChange={(e) => setCardToEditPaymentTypeType(e.target.value)}
                                          >
                                            {Object.keys(paymentTypes).length > 0 &&
                                              paymentTypes.map((paymentType) => (
                                                <MenuItem key={paymentType.id} value={paymentType.type}>
                                                  {paymentType.name}
                                                </MenuItem>
                                              ))}
                                          </Select>
                                        </FormControl>
                                      </Grid>

                                      {/* Card payment status */}
                                      <Grid item xs={12}>
                                        <FormControl color="secondary" sx={{ width: '100%' }}>
                                          <InputLabel>Stato Pagamento</InputLabel>
                                          <Select
                                            sx={{ width: '100%' }}
                                            value={cardToEditPaymentStatusType}
                                            disabled={applyPaymentStatusFilter}
                                            label="Stato Pagamento"
                                            onChange={(e) => setCardToEditPaymentStatusType(e.target.value)}
                                          >
                                            {Object.keys(paymentStatus).length > 0 &&
                                              paymentStatus
                                                .filter((ps) => ps.type !== 'C')
                                                .map((ps) => (
                                                  <MenuItem key={ps.id} value={ps.type}>
                                                    {ps.name}
                                                  </MenuItem>
                                                ))}
                                          </Select>
                                        </FormControl>
                                      </Grid>

                                      {cardToEditPaymentTypeType === 'BB' && (
                                        <Grid item xs={12}>
                                          <DefaultOutlinedButton onClick={sendBankDataEmail} startIcon={<EmailIcon />}>
                                            Invia coordinate bancarie
                                          </DefaultOutlinedButton>
                                        </Grid>
                                      )}

                                      {cardToEditPaymentTypeType === 'PB' && (
                                        <>
                                          <Grid item xs={12}>
                                            <CustomTextField
                                              name="terminalID"
                                              label="ID Terminale"
                                              value={cardToEditTerminalCode}
                                              onChange={(e) => setCardToEditTerminalCode(e.currentTarget.value)}
                                            />
                                          </Grid>

                                          <Grid item xs={12}>
                                            <CustomTextField
                                              name="authCode"
                                              label="Codice Autorizzazione"
                                              value={cardToEditAuthCode}
                                              onChange={(e) => setCardToEditAuthCode(e.currentTarget.value)}
                                            />
                                          </Grid>
                                        </>
                                      )}

                                      {/* {cardToEditPaymentTypeType === 'PCC' && (
                                        <>
                                          <Grid item xs={12}>
                                            <CustomTextField
                                              name="transactionID"
                                              label="ID Transazione"
                                              disabled={applyPaymentStatusFilter}
                                              value={cardToEditTransactionID}
                                              onChange={(e) => setCardToEditTransactionID(e.currentTarget.value)}
                                            />
                                          </Grid>
                                        </>
                                      )} */}
                                    </Grid>

                                    {isDialogOpen === 'editCard' && displayPopup()}
                                  </DialogContent>
                                  <DialogActions>
                                    <DefaultOutlinedButton onClick={handleCloseEditCard}>Chiudi</DefaultOutlinedButton>
                                    <DefaultContainedButton type="submit">Salva</DefaultContainedButton>
                                  </DialogActions>
                                </form>
                              </>
                            ) : (
                              LoadingUI()
                            )}
                          </Dialog>

                          {/* Delete Card */}
                          <Dialog open={openDeleteCard} fullWidth onClose={() => {}}>
                            <DialogTitle sx={{ textAlign: 'center' }}>Eliminazione Tessera</DialogTitle>

                            <DialogContent>
                              <Typography variant="span">
                                Sei sicuro di voler eliminare la tessera numero {cardToDelete.prefix}
                                {cardToDelete.card_number}?
                              </Typography>
                              {isDialogOpen === 'deleteCard' && displayPopup()}
                            </DialogContent>
                            <DialogActions>
                              <DefaultOutlinedButton onClick={handleCloseDeleteCard}>Chiudi</DefaultOutlinedButton>
                              <DefaultContainedButton onClick={handleDeleteCard}>Elimina</DefaultContainedButton>
                            </DialogActions>
                          </Dialog>

                          {cards.map((card) => (
                            <TableRow hover key={card.card_id}>
                              <TableCell>{card.campaign_name}</TableCell>
                              <TableCell>
                                <CommonLabel
                                  textColor={dayjs(card.end_date) > dayjs(new Date()) ? '#118d57' : '#b71d18'}
                                  bgColor={dayjs(card.end_date) > dayjs(new Date()) ? '#d3efdf' : '#f7ddd8'}
                                  text={dayjs(card.end_date) > dayjs(new Date()) ? 'Attiva' : 'Scaduta'}
                                />
                              </TableCell>
                              <TableCell>{card.payment_entity}</TableCell>
                              <TableCell>{dayjs(card.creation_date).format('DD/MM/YYYY')}</TableCell>
                              <TableCell>
                                {card.prefix}
                                {card.card_number}
                              </TableCell>
                              <TableCell>{card.payment_type ? card.payment_type.name : '//'}</TableCell>
                              <TableCell>{`${card.price?.toFixed(2)}€`}</TableCell>
                              <TableCell>
                                <CommonLabel
                                  textColor={card.payment_status.type === 'P' ? '#118d57' : '#b71d18'}
                                  bgColor={card.payment_status.type === 'P' ? '#d3efdf' : '#f7ddd8'}
                                  text={card.payment_status.name}
                                />
                              </TableCell>
                              <TableCell>
                                <Stack
                                  sx={{
                                    display: canManageUser
                                      ? 'flex'
                                      : !canManageUser && card.payment_status.type === 'P'
                                      ? 'none'
                                      : 'flex',
                                  }}
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="end"
                                >
                                  {/* Edit Card */}
                                  <Tooltip title="Aggiorna Tessera" arrow placement="top">
                                    <Box>
                                      <CustomIconButton
                                        disabled={!canModifyUser}
                                        onClick={() => handleOpenEditCard(card)}
                                      >
                                        <EditIcon />
                                      </CustomIconButton>
                                    </Box>
                                  </Tooltip>

                                  {/* Delete Card */}
                                  {canDeleteCards && (
                                    <Tooltip title="Elimina Tessera" arrow placement="top">
                                      <Box>
                                        <CustomIconButton onClick={() => handleOpenDeleteCard(card)}>
                                          <DeleteIcon />
                                        </CustomIconButton>
                                      </Box>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      ) : (
                        <TableBody>
                          <TableRow>
                            <TableCell colSpan={9} align="center">
                              Nessuna tessera disponibile
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>
                </Card>
              </>
            ) : (
              // Error handler
              ErrorUI(errorMessage)
            )}
          </QuintupleTabPanel>

          {/* Positions tab */}
          <QuintupleTabPanel value={value} index={2}>
            {/* Loading */}
            {isLoading ? (
              LoadingUI()
            ) : errorMessage.length === 0 ? (
              <Box>
                <Stack flexDirection="row" alignItems="center" justifyContent="end" sx={{ paddingBottom: '24px' }}>
                  {/* New position button */}
                  <DefaultContainedButton
                    disabled={!canModifyUser}
                    onClick={handleOpenNewPosition}
                    startIcon={<AddIcon />}
                  >
                    Aggiungi Carica
                  </DefaultContainedButton>

                  <NewPositionDialog
                    openDialog={openNewPosition}
                    closeDialog={handleCloseNewPosition}
                    userAuth={'admin'}
                    userInfo={userInfo}
                    refreshFunction={getUserPositions}
                  />
                </Stack>

                {/* Positions card */}
                <Card>
                  {/* Filter Tabs */}
                  <Tabs
                    variant="fullWidth"
                    value={positionTabValue}
                    onChange={handleChangePositionTab}
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    textColor="secondary"
                    indicatorColor="secondary"
                    sx={{ boxShadow: 'rgba(145, 158, 171, 0.08) 0px -2px 0px 0px inset', padding: '0px 24px 0' }}
                  >
                    <Tab
                      disableRipple
                      icon={
                        <Typography
                          sx={{
                            backgroundColor: positionTabValue === 0 ? '#000000' : '#edeff1',
                            borderRadius: '6px',
                            color: positionTabValue === 0 ? '#ffffff' : '#637381',
                            padding: '2px 8px',
                            fontSize: '1em',
                            fontWeight: 'bold',
                          }}
                        >
                          {extPositions.length}
                        </Typography>
                      }
                      iconPosition="end"
                      label="Cariche Istituzionali"
                    />
                    <Tab
                      disableRipple
                      icon={
                        <Typography
                          sx={{
                            backgroundColor: positionTabValue === 1 ? '#22c55e' : '#dbf6e5',
                            borderRadius: '6px',
                            color: positionTabValue === 1 ? '#ffffff' : '#118d57',
                            padding: '2px 8px',
                            fontSize: '1em',
                            fontWeight: 'bold',
                          }}
                        >
                          {intPositions.length}
                        </Typography>
                      }
                      iconPosition="end"
                      label="Cariche Politiche"
                    />
                  </Tabs>

                  {/* Table */}
                  <TableContainer sx={{ padding: '24px' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Stato Carica</TableCell>
                          <TableCell>Carica</TableCell>
                          {positionTabValue === 0 && <TableCell>Email Istituzionale</TableCell>}
                          {positionTabValue === 1 && <TableCell>Entità</TableCell>}
                          <TableCell>Data Inizio</TableCell>
                          <TableCell>Data Fine</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>

                      {displayedPositions.length > 0 ? (
                        <TableBody>
                          <EditPositionDialog
                            openDialog={openEditPosition}
                            closeDialog={handleCloseEditPosition}
                            userAuth={'admin'}
                            userInfo={userInfo}
                            positionToEdit={positionToEdit}
                            setPositionToEdit={setPositionToEdit}
                            refreshFunction={getUserPositions}
                          />

                          {/* Delete Position Dialog */}
                          <DecisionDialog
                            openDialog={openDeleteCharge}
                            dialogTitle={'Elimina carica'}
                            dialogDescription={`Sei sicuro di voler eliminare la carica: ${positionToEdit.name} ?`}
                            closeOperation={onCloseDeleteChargeDialog}
                            requestOperation={onDeleteCharge}
                            requestText={'Elimina'}
                            closeText={'Chiudi'}
                            RequestButton={RedOutlinedButton}
                          />

                          {displayedPositions
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((position) => (
                              <TableRow key={position.id}>
                                <TableCell>{PositionStatusLabel(position, 'label')}</TableCell>

                                <TableCell>{position.name}</TableCell>

                                {positionTabValue === 0 && (
                                  <TableCell>{position.email ? position.email : '/'}</TableCell>
                                )}

                                {positionTabValue === 1 && <TableCell>{position.entity_name}</TableCell>}

                                <TableCell>
                                  {position.start_date ? dayjs(position.start_date).format('DD/MM/YYYY') : '/'}
                                </TableCell>

                                <TableCell>
                                  {position.end_date ? dayjs(position.end_date).format('DD/MM/YYYY') : '/'}
                                </TableCell>

                                <TableCell
                                  align="right"
                                  sx={{ padding: '4px', paddingLeft: '16px', maxWidth: '150px' }}
                                >
                                  <Stack
                                    direction="row"
                                    justifyContent="end"
                                    alignItems="center"
                                    gap="12px"
                                    maxWidth="150px"
                                  >
                                    {/* Edit Position */}
                                    <Box>
                                      <Tooltip title="Modifica Carica" arrow placement="top">
                                        <Box>
                                          <CustomIconButton
                                            disabled={!canModifyUser}
                                            onClick={() => handleOpenEditPosition(position)}
                                          >
                                            <EditIcon />
                                          </CustomIconButton>
                                        </Box>
                                      </Tooltip>
                                    </Box>

                                    {/* Delete Position */}
                                    <Box>
                                      <Tooltip title="Elimina Carica" arrow placement="top">
                                        <Box>
                                          <CustomIconButton
                                            disabled={!canModifyUser}
                                            onClick={() => onOpenDeleteChargeDialog(position)}
                                          >
                                            <DeleteIcon />
                                          </CustomIconButton>
                                        </Box>
                                      </Tooltip>
                                    </Box>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))}

                          {/* empty table rows */}
                          {emptyRows > 0 && (
                            <TableRow style={{ height: 57 * emptyRows }}>
                              <TableCell colSpan={8} />
                            </TableRow>
                          )}
                        </TableBody>
                      ) : (
                        <TableBody>
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              Nessuna carica disponibile
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      )}

                      {/*  pagination */}
                      <TableFooter>
                        <TableRow>
                          <ThemeProvider theme={theme}>
                            <TablePagination
                              sx={{ borderBottom: 'none' }}
                              rowsPerPageOptions={[]}
                              colSpan={8}
                              count={positions.length}
                              rowsPerPage={rowsPerPage}
                              page={page}
                              onPageChange={handleChangePage}
                              onRowsPerPageChange={handleChangeRowsPerPage}
                              labelRowsPerPage="Righe per Pagina"
                              ActionsComponent={TablePaginationActions}
                            />
                          </ThemeProvider>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                </Card>
              </Box>
            ) : (
              // Error handler
              ErrorUI(errorMessage)
            )}
          </QuintupleTabPanel>

          <QuintupleTabPanel value={value} index={3}>
            <Grid container spacing={3} sx={{ marginTop: '24px' }}>
              <Grid item xs={12} sx={{ paddingTop: '0px !important' }}>
                <Card>
                  {/* Authorization table */}
                  <TableContainer sx={{ padding: '24px 24px 6px' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Badge</TableCell>
                          <TableCell>Entità</TableCell>
                          <TableCell>Data Iscrizione</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2">Nessun badge disponibile</Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <ThemeProvider theme={theme}>
                            <TablePagination
                              sx={{ borderBottom: 'none' }}
                              rowsPerPageOptions={[]}
                              colSpan={6}
                              count={authorizationList.length}
                              rowsPerPage={authRowsPerPage}
                              page={authPage}
                              onPageChange={handleAuthPage}
                              onRowsPerPageChange={handleAuthRowsPerPage}
                              labelRowsPerPage="Righe per Pagina"
                              ActionsComponent={TablePaginationActions}
                            />
                          </ThemeProvider>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                </Card>
              </Grid>
            </Grid>
          </QuintupleTabPanel>

          {/* Authorization tab */}
          <QuintupleTabPanel value={value} index={4}>
            {isLoading || !authApiCalled ? (
              LoadingUI()
            ) : errorMessage.length === 0 ? (
              <>
                <Stack direction="row" justifyContent="end" gap="10px">
                  <DefaultContainedButton
                    disabled={!canManageUserRoles}
                    startIcon={<AddIcon />}
                    onClick={handleOpenNewAuthorizationDialog}
                  >
                    Aggiungi Autorizzazione
                  </DefaultContainedButton>

                  {/* new authorization dialog */}
                  <Dialog fullWidth open={openNewAuthorization} onClose={() => {}}>
                    <DialogTitle sx={{ textAlign: 'center' }}>Aggiungi Autorizzazione</DialogTitle>
                    <form onSubmit={onAddAuthorization}>
                      <DialogContent>
                        {authDataCalled ? (
                          <Grid container spacing={3}>
                            {/* Entità */}
                            <Grid item xs={12}>
                              {!newAuthEntity ? (
                                <DefaultOutlinedButton
                                  customStyles={{ width: '100%' }}
                                  onClick={() => setOpenNewAuthEntitiesFilter(!openNewAuthEntitiesFilter)}
                                >
                                  Cerca Entità
                                </DefaultOutlinedButton>
                              ) : (
                                <Grid container spacing={3}>
                                  <Grid item xs={12}>
                                    <CustomTextField
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                                            <Tooltip title="Modifica" placement="left" arrow>
                                              <EditIcon
                                                onClick={() => setOpenNewAuthEntitiesFilter(!openNewAuthEntitiesFilter)}
                                              />
                                            </Tooltip>
                                          </InputAdornment>
                                        ),
                                      }}
                                      value={newAuthEntity.name}
                                      label="Entità"
                                    />
                                  </Grid>
                                </Grid>
                              )}

                              <EntitiesDialog
                                openDialog={openNewAuthEntitiesFilter}
                                closeDialog={handleCloseNewAuthEntitiesFilterDialog}
                                outputEntity={getNewAuthEntity}
                                context={'userAuth'}
                              />
                            </Grid>

                            {/* Oggetto */}
                            <Grid item xs={12} sx={{ overflow: 'auto' }}>
                              <CustomAutocomplete
                                required
                                id="auth-object"
                                options={authObjList}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                getOptionLabel={(option) => option.name}
                                renderOption={(props, option) => (
                                  <Box component="li" {...props} key={option.id}>
                                    {option.name}
                                  </Box>
                                )}
                                onChange={(e, value) => setNewAuthObject(value)}
                                renderInput={(params) => <TextField {...params} label="Oggetto *" variant="outlined" />}
                              />
                            </Grid>

                            {/* Tipo di autorizzazione */}
                            <Grid item xs={12} sx={{ overflow: 'auto' }}>
                              <FormGroup
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  justifyContent: 'start',
                                  alignItems: 'center',
                                }}
                              >
                                {authTypeList.map((authType) => (
                                  <FormControlLabel
                                    key={authType.type}
                                    control={
                                      <Checkbox
                                        checked={newAuthType.includes(authType.type)}
                                        onChange={(event) => handleCheckboxChange(event, authType)}
                                      />
                                    }
                                    label={getAuthTypeCheckboxLabel(authType.type)}
                                  />
                                ))}
                              </FormGroup>
                            </Grid>

                            {/* Start Date */}
                            {/* <Grid item xs={12} md={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <CustomDatePicker
                                required
                                label="Data Inizio Autorizzazione"
                                onChange={handleNewAuthStartDate}
                                maxDate={dayjs(newAuthEndDate)}
                              />
                            </LocalizationProvider>
                          </Grid> */}

                            {/* End Date */}
                            {/* <Grid item xs={12} md={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <CustomDatePicker
                                label="Data Fine Autorizzazione"
                                onChange={handleNewAuthEndDate}
                                minDate={dayjs(newAuthStartDate)}
                              />
                            </LocalizationProvider>
                          </Grid> */}
                          </Grid>
                        ) : (
                          LoadingUI()
                        )}
                      </DialogContent>
                      <DialogActions>
                        <DefaultOutlinedButton onClick={handleCloseNewAuthorizationDialog}>
                          Chiudi
                        </DefaultOutlinedButton>
                        <DefaultContainedButton type="submit">Aggiungi</DefaultContainedButton>

                        <CustomSnackbar
                          open={openNewAuthDataSnack}
                          handleClose={handleCloseNewAuthDataSnack}
                          message="Inserisci i dati mancanti!"
                        />

                        {isDialogOpen === 'newAuth' && displayPopup()}
                      </DialogActions>
                    </form>
                  </Dialog>
                </Stack>

                {/* show authorization dialog */}
                <Dialog fullWidth open={openShowAuthorization} onClose={() => {}}>
                  <DialogTitle>Visualizzazione Autorizzazione</DialogTitle>
                  <DialogContent>
                    <Grid container spacing={3} sx={{ marginTop: '1px' }}>
                      <Grid item xs={12}>
                        <CustomTextField
                          label="Entità"
                          inputProps={{ readOnly: true }}
                          value={authorizationToShow.entity ? authorizationToShow.entity.name : ''}
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <CustomTextField
                          label="Oggetto Autorizzazione"
                          inputProps={{ readOnly: true }}
                          value={
                            authorizationToShow.authorization_object
                              ? authorizationToShow.authorization_object.name
                              : ''
                          }
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <CustomTextField
                          label="Tipo Autorizzazione"
                          inputProps={{ readOnly: true }}
                          value={
                            authorizationToShow.authorization_type
                              ? authorizationToShow.authorization_type.description
                              : ''
                          }
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <CustomTextField
                          label="Data Creazione"
                          inputProps={{ readOnly: true }}
                          value={
                            authorizationToShow.created_at
                              ? dayjs(authorizationToShow.created_at).format('DD/MM/YYYY')
                              : ''
                          }
                        />
                      </Grid>
                    </Grid>
                  </DialogContent>
                  <DialogActions>
                    <DefaultOutlinedButton onClick={handleCloseShowAuthorizationDialog}>Chiudi</DefaultOutlinedButton>
                  </DialogActions>
                </Dialog>

                {/* delete authorization dialog */}
                <Dialog fullWidth open={openDeleteAuthorization} onClose={() => {}}>
                  <DialogTitle>Eliminazione Autorizzazione</DialogTitle>
                  <DialogContent>Sei sicuro di voler eliminare questa autorizzazione?</DialogContent>
                  <DialogActions>
                    <DefaultOutlinedButton onClick={handleCloseDeleteAuthorizationDialog}>Chiudi</DefaultOutlinedButton>
                    <DefaultContainedButton onClick={() => handleDeleteAuthorization()}>Elimina</DefaultContainedButton>

                    {isDialogOpen === 'deleteAuth' && displayPopup()}
                  </DialogActions>
                </Dialog>

                <Grid container spacing={3} sx={{ marginTop: '24px' }}>
                  <Grid item xs={12} sx={{ paddingTop: '0px !important' }}>
                    <Card>
                      {/* Authorization table */}
                      <TableContainer sx={{ padding: '24px 24px 6px' }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Entità</TableCell>
                              <TableCell>Oggetto Autorizzazione</TableCell>
                              <TableCell>Tipo Autorizzazione</TableCell>
                              <TableCell>Data Creazione</TableCell>
                              <TableCell />
                            </TableRow>
                          </TableHead>
                          {authorizationList.length > 0 ? (
                            <TableBody>
                              {authorizationList
                                .slice(authPage * authRowsPerPage, authPage * authRowsPerPage + authRowsPerPage)
                                .map((auth) => (
                                  <TableRow hover key={auth.id}>
                                    <TableCell>{auth.entity.name}</TableCell>
                                    <TableCell>{auth.authorization_object.name}</TableCell>
                                    <TableCell>{auth.authorization_type.description}</TableCell>
                                    <TableCell>{dayjs(auth.created_at).format('DD/MM/YYYY')}</TableCell>
                                    <TableCell>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          flexDirection: 'row',
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Tooltip title="Visualizza dettaglio" arrow>
                                          <Box>
                                            <CustomIconButton onClick={() => handleOpenShowAuthorizationDialog(auth)}>
                                              <DescriptionOutlinedIcon />
                                            </CustomIconButton>
                                          </Box>
                                        </Tooltip>

                                        <Tooltip title="Elimina" arrow>
                                          <Box>
                                            <CustomIconButton
                                              disabled={!canManageUserRoles}
                                              onClick={() => handleOpenDeleteAuthorizationDialog(auth)}
                                            >
                                              <DeleteIcon />
                                            </CustomIconButton>
                                          </Box>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))}

                              {emptyAuthRows > 0 && (
                                <TableRow style={{ height: 55 * emptyAuthRows }}>
                                  <TableCell colSpan={6} />
                                </TableRow>
                              )}
                            </TableBody>
                          ) : (
                            // No Authorization View
                            <TableBody>
                              <TableRow>
                                <TableCell colSpan={6} align="center">
                                  <Typography variant="body2">Nessuna autorizzazione disponibile</Typography>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          )}
                          <TableFooter>
                            <TableRow>
                              <ThemeProvider theme={theme}>
                                <TablePagination
                                  sx={{ borderBottom: 'none' }}
                                  rowsPerPageOptions={[]}
                                  colSpan={6}
                                  count={authorizationList.length}
                                  rowsPerPage={authRowsPerPage}
                                  page={authPage}
                                  onPageChange={handleAuthPage}
                                  onRowsPerPageChange={handleAuthRowsPerPage}
                                  labelRowsPerPage="Righe per Pagina"
                                  ActionsComponent={TablePaginationActions}
                                />
                              </ThemeProvider>
                            </TableRow>
                          </TableFooter>
                        </Table>
                      </TableContainer>
                    </Card>
                  </Grid>
                </Grid>
              </>
            ) : (
              // Error handler
              ErrorUI(errorMessage)
            )}
          </QuintupleTabPanel>
        </Box>

        {/* Popup component */}
        {isDialogOpen === 'noDialog' && (
          <Container ref={setAnchor}>
            <Popup anchor={anchor} open={open}>
              {popupSuccess ? (
                <PopupSuccessBody sx={{ whiteSpace: 'pre-line' }}>{popUpText}</PopupSuccessBody>
              ) : (
                <PopupErrorBody>{popUpText}</PopupErrorBody>
              )}
            </Popup>
          </Container>
        )}
      </Container>

      {/* snackbar */}
      <Snackbar
        open={onOpenSnackbar}
        onClose={onSnackBarClose}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbarData.color} onClose={onSnackBarClose}>
          {snackbarData.text}
        </Alert>
      </Snackbar>
    </div>
  );
}
